import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_API_URL || '/api';

export default function Admin() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', role: 'mitarbeiter' });
  const [objekte, setObjekte] = useState([]);
  const [newObjekt, setNewObjekt] = useState({ objekt_nr: '', adresse: '' });
  const [showAddObjekt, setShowAddObjekt] = useState(false);
  const [objektFilter, setObjektFilter] = useState('');
  const [editObjekt, setEditObjekt] = useState(null);
  const [activeTab, setActiveTab] = useState('benutzer');

  // Tickets
  const [tickets, setTickets] = useState([]);
  const [ticketStats, setTicketStats] = useState(null);
  const [ticketFilter, setTicketFilter] = useState('');
  const [ticketStatusFilter, setTicketStatusFilter] = useState('');
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [editNotiz, setEditNotiz] = useState('');

  // Freigaben
  const [freigaben, setFreigaben] = useState([]);
  const [freigabeStats, setFreigabeStats] = useState(null);
  const [freigabeDetail, setFreigabeDetail] = useState(null); // { freigabe, execution }
  const [freigabeDetailLoading, setFreigabeDetailLoading] = useState(false);

  const loadUsers = () => {
    fetch(`${API_URL}/admin/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setUsers).catch(() => {});
  };
  const loadStats = () => {
    fetch(`${API_URL}/admin/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setStats).catch(() => {});
  };
  const loadObjekte = () => {
    fetch(`${API_URL}/admin/objekte`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setObjekte).catch(() => {});
  };
  const loadTickets = () => {
    const qs = ticketStatusFilter ? `?status=${ticketStatusFilter}` : '';
    fetch(`${API_URL}/feedback/tickets${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setTickets).catch(() => {});
  };
  const loadTicketStats = () => {
    fetch(`${API_URL}/feedback/tickets/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setTicketStats).catch(() => {});
  };
  const loadFreigaben = () => {
    fetch(`${API_URL}/freigabe/pending`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setFreigaben).catch(() => {});
  };
  const loadFreigabeStats = () => {
    fetch(`${API_URL}/freigabe/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(setFreigabeStats).catch(() => {});
  };
  useEffect(() => { loadUsers(); loadStats(); loadObjekte(); loadTickets(); loadTicketStats(); loadFreigaben(); loadFreigabeStats(); }, [token]);
  useEffect(() => { loadTickets(); }, [ticketStatusFilter]);

  const addUser = async () => {
    const res = await fetch(`${API_URL}/admin/users`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(newUser),
    });
    if (res.ok) {
      setShowAddUser(false);
      setNewUser({ email: '', name: '', role: 'mitarbeiter' });
      loadUsers();
    }
  };

  const toggleUser = async (userId, isActive) => {
    await fetch(`${API_URL}/admin/users/${userId}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ is_active: !isActive }),
    });
    loadUsers();
  };

  const changeRole = async (userId, role) => {
    await fetch(`${API_URL}/admin/users/${userId}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });
    loadUsers();
  };

  const roleColors = {
    admin: { bg: 'var(--brand-danger-bg)', text: 'var(--brand-danger)' },
    manager: { bg: 'var(--brand-gold-10)', text: 'var(--brand-gold)' },
    entwickler: { bg: '#e8f0fe', text: '#1a73e8' },
    mitarbeiter: { bg: 'var(--brand-info-bg)', text: 'var(--brand-info)' },
    readonly: { bg: 'var(--brand-cream)', text: 'var(--brand-text-muted)' },
  };
  const roleLabels = { admin: 'Admin', manager: 'Teamleitung', entwickler: 'Entwickler', mitarbeiter: 'Mitarbeiter', readonly: 'Readonly' };

  const addObjekt = async () => {
    if (!newObjekt.objekt_nr.trim()) return;
    const res = await fetch(`${API_URL}/admin/objekte`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(newObjekt),
    });
    if (res.ok) {
      setShowAddObjekt(false);
      setNewObjekt({ objekt_nr: '', adresse: '' });
      loadObjekte();
    }
  };

  const deleteObjekt = async (id) => {
    await fetch(`${API_URL}/admin/objekte/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    loadObjekte();
  };

  const saveObjekt = async () => {
    if (!editObjekt) return;
    await fetch(`${API_URL}/admin/objekte/${editObjekt.id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ objekt_nr: editObjekt.objekt_nr, adresse: editObjekt.adresse, verwaltungsart: editObjekt.verwaltungsart }),
    });
    setEditObjekt(null);
    loadObjekte();
  };

  const toggleObjekt = async (id) => {
    await fetch(`${API_URL}/admin/objekte/${id}/toggle`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
    });
    loadObjekte();
  };

  const filteredObjekte = objekte.filter(o =>
    !objektFilter || o.objekt_nr.includes(objektFilter) || o.adresse.toLowerCase().includes(objektFilter.toLowerCase())
  );

  const inputStyle = {
    width: '100%', padding: 8, border: '1px solid var(--brand-warm-gray)',
    borderRadius: 4, fontSize: 13, fontFamily: 'var(--font-body)',
  };

  const [objektPage, setObjektPage] = useState(0);
  const OBJEKTE_PER_PAGE = 20;

  const handleFreigabeDecision = async (id, decision) => {
    const kommentar = decision === 'abgelehnt' ? (window.prompt('Kommentar (optional):') || '') : '';
    try {
      const res = await fetch(`${API_URL}/freigabe/${id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: decision, admin_kommentar: kommentar }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert('Fehler: ' + (err.detail || 'Unbekannter Fehler'));
        return;
      }
    } catch (e) {
      alert('Netzwerkfehler: ' + e.message);
      return;
    }
    loadFreigaben(); loadFreigabeStats();
    if (freigabeDetail?.freigabe?.id === id) setFreigabeDetail(null);
  };

  const handleFreigabeDelete = async (id) => {
    if (!window.confirm('Freigabe-Eintrag wirklich loeschen?')) return;
    await fetch(`${API_URL}/freigabe/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    loadFreigaben(); loadFreigabeStats();
    if (freigabeDetail?.freigabe?.id === id) setFreigabeDetail(null);
  };

  const [detailEditMode, setDetailEditMode] = useState(false);
  const [detailEditData, setDetailEditData] = useState({});
  const [detailShowReport, setDetailShowReport] = useState(false);

  const loadFreigabeDetail = async (f) => {
    setFreigabeDetailLoading(true);
    setDetailEditMode(false);
    setDetailShowReport(false);
    try {
      const res = await fetch(`${API_URL}/skills/executions/${f.resource_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const exec = await res.json();
        setFreigabeDetail({ freigabe: f, execution: exec });
        setDetailEditData(exec.result?.extracted_data || {});
      }
    } catch (err) {}
    setFreigabeDetailLoading(false);
  };

  const saveDetailEdit = async () => {
    if (!freigabeDetail) return;
    const res = await fetch(`${API_URL}/skills/executions/${freigabeDetail.execution.id}/data`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ extracted_data: detailEditData }),
    });
    if (res.ok) {
      // Reload
      await loadFreigabeDetail(freigabeDetail.freigabe);
      setDetailEditMode(false);
    }
  };

  // KI-Kosten State
  const [aiCosts, setAiCosts] = useState(null);
  const [aiCostsMonth, setAiCostsMonth] = useState('');
  const [aiCostsLoading, setAiCostsLoading] = useState(false);
  const [briefingSettings, setBriefingSettings] = useState(null);
  const [newDailyLimit, setNewDailyLimit] = useState('');

  const loadAiCosts = (month) => {
    setAiCostsLoading(true);
    const qs = month ? `?month=${month}` : '';
    fetch(`${API_URL}/mail/ai-costs${qs}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(data => { setAiCosts(data); setAiCostsLoading(false); }).catch(() => setAiCostsLoading(false));
  };
  const loadBriefingSettings = () => {
    fetch(`${API_URL}/mail/briefing/settings`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(data => { setBriefingSettings(data); setNewDailyLimit(String(data.daily_limit)); }).catch(() => {});
  };
  const saveDailyLimit = () => {
    const val = parseInt(newDailyLimit);
    if (isNaN(val) || val < 0) return;
    fetch(`${API_URL}/mail/briefing/settings`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ daily_limit: val }),
    }).then(r => r.json()).then(() => loadBriefingSettings()).catch(() => {});
  };

  const tabs = [
    { id: 'benutzer', label: 'Benutzer', icon: '\uD83D\uDC65' },
    { id: 'berechtigungen', label: 'Skill-Berechtigungen', icon: '\uD83D\uDD10' },
    { id: 'freigaben', label: 'Freigaben', icon: '\u2705', badge: freigabeStats?.angefragt || 0 },
    { id: 'tickets', label: 'Tickets', icon: '\uD83C\uDFAB', badge: ticketStats?.offen || 0 },
    { id: 'objekte', label: 'Objekt-IDs', icon: '\uD83C\uDFE2' },
    { id: 'ki-kosten', label: 'KI-Kosten', icon: '\uD83D\uDCB0' },
    { id: 'mail-sichtbarkeit', label: 'E-Mail-Sichtbarkeit', icon: '\uD83D\uDC41\uFE0F' },
    { id: 'vorlagen', label: 'SB-Vorlagen', icon: '\uD83D\uDCE2' },
    { id: 'doc-ai-kappung', label: 'Doc-AI Kappung', icon: '\u2702\uFE0F' },
  ];

  // Skill-Berechtigungen State
  const [permData, setPermData] = useState(null);
  const [permLoading, setPermLoading] = useState(false);

  const loadPermissions = () => {
    setPermLoading(true);
    fetch(`${API_URL}/admin/skill-permissions`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { setPermData(d); setPermLoading(false); })
      .catch(() => setPermLoading(false));
  };

  useEffect(() => { if (activeTab === 'berechtigungen') loadPermissions(); }, [activeTab]);
  useEffect(() => { if (activeTab === 'ki-kosten') { loadAiCosts(aiCostsMonth); loadBriefingSettings(); } }, [activeTab]);

  const togglePerm = async (role, skillId, field) => {
    if (!permData) return;
    const current = permData.matrix[role]?.[skillId]?.[field] ?? true;
    const newVal = !current;
    // Optimistic update
    setPermData(prev => {
      const next = { ...prev, matrix: { ...prev.matrix } };
      next.matrix[role] = { ...next.matrix[role] };
      next.matrix[role][skillId] = { ...next.matrix[role][skillId], [field]: newVal };
      // Wenn can_view=false, dann auch can_execute=false
      if (field === 'can_view' && !newVal) next.matrix[role][skillId].can_execute = false;
      // Wenn can_execute=true, dann auch can_view=true
      if (field === 'can_execute' && newVal) next.matrix[role][skillId].can_view = true;
      return next;
    });
    const body = { role, skill_id: skillId, can_view: newVal, can_execute: permData.matrix[role]?.[skillId]?.can_execute ?? true };
    if (field === 'can_view' && !newVal) body.can_execute = false;
    if (field === 'can_execute') { body.can_execute = newVal; if (newVal) body.can_view = true; }
    await fetch(`${API_URL}/admin/skill-permissions`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
  };

  const toggleDeveloperChatUser = async (entry) => {
    if (!permData) return;
    const canView = !entry.can_view;
    setPermData(prev => ({
      ...prev,
      developer_chat_users: (prev.developer_chat_users || []).map(u => (
        u.user_id === entry.user_id
          ? { ...u, can_view: canView, can_execute: canView }
          : u
      )),
    }));
    await fetch(`${API_URL}/admin/developer-chat/users/${entry.user_id}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ can_view: canView, can_execute: canView }),
    });
  };

  return (
    <div>
      <h1 style={{
        fontFamily: 'var(--font-headline)', fontSize: 24, fontWeight: 400,
        color: 'var(--brand-navy)', marginBottom: 20,
      }}>Verwaltung</h1>

      {/* Tab-Navigation */}
      <div className="admin-tab-nav">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px', fontSize: 13, fontWeight: activeTab === tab.id ? 600 : 400,
              fontFamily: 'var(--font-body)', cursor: 'pointer',
              background: 'transparent', border: 'none',
              color: activeTab === tab.id ? 'var(--brand-navy)' : 'var(--brand-text-muted)',
              borderBottom: activeTab === tab.id ? '2px solid var(--brand-gold)' : '2px solid transparent',
              marginBottom: -2, transition: 'all 0.15s',
              display: 'flex', alignItems: 'center', gap: 6,
            }}
          >
            <span style={{ fontSize: 14 }}>{tab.icon}</span>
            {tab.label}
            {tab.badge > 0 && (
              <span style={{
                padding: '1px 7px', borderRadius: 10, fontSize: 10, fontWeight: 700,
                background: 'var(--brand-danger)', color: '#fff', marginLeft: 2,
              }}>{tab.badge}</span>
            )}
          </button>
        ))}
      </div>

      {/* ══ Tab: Benutzer ══ */}
      {activeTab === 'benutzer' && (<>
      {/* Stats */}
      {stats && (
        <div className="stats-grid-3" style={{ marginBottom: 28 }}>
          {[
            { label: 'Benutzer', value: stats.total_users, color: 'var(--brand-gold)' },
            { label: 'Aktiv', value: stats.active_users, color: 'var(--brand-success)' },
            { label: 'Skill-Ausführungen', value: stats.total_executions, color: 'var(--brand-info)' },
          ].map(s => (
            <div key={s.label} style={{
              background: '#fff', borderRadius: 8, padding: '1rem 1.25rem',
              border: '1px solid var(--brand-warm-gray)', position: 'relative', overflow: 'hidden',
            }}>
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: s.color }} />
              <div style={{
                fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.8px',
                color: 'var(--brand-text-muted)', fontWeight: 500, marginBottom: 6,
              }}>{s.label}</div>
              <div style={{ fontFamily: 'var(--font-headline)', fontSize: 28, color: 'var(--brand-navy)' }}>
                {s.value}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Users Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16,
      }}>
        <h2 style={{
          fontFamily: 'var(--font-headline)', fontSize: 18, fontWeight: 400, color: 'var(--brand-navy)',
        }}>Benutzer</h2>
        <button
          onClick={() => setShowAddUser(true)}
          style={{
            padding: '8px 16px', fontSize: 12, fontWeight: 600,
            background: 'var(--brand-gold)', color: 'var(--brand-navy)',
            border: 'none', borderRadius: 4, cursor: 'pointer',
            textTransform: 'uppercase', letterSpacing: '0.5px',
            fontFamily: 'var(--font-body)',
          }}
        >
          + Benutzer anlegen
        </button>
      </div>

      {/* Add User */}
      {showAddUser && (
        <div style={{
          background: '#fff', borderRadius: 8, padding: 20,
          border: '1px solid var(--brand-gold)', marginBottom: 16,
          display: 'flex', gap: 12, alignItems: 'flex-end',
        }}>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 4, color: 'var(--brand-navy)' }}>E-Mail</label>
            <input value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))}
              placeholder="name@fleet-immobilien.de" style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 4, color: 'var(--brand-navy)' }}>Name</label>
            <input value={newUser.name} onChange={e => setNewUser(p => ({ ...p, name: e.target.value }))}
              placeholder="Vor- und Nachname" style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 4, color: 'var(--brand-navy)' }}>Rolle</label>
            <select value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}
              style={{ ...inputStyle, background: '#fff' }}>
              <option value="readonly">Readonly</option>
              <option value="mitarbeiter">Mitarbeiter</option>
              <option value="entwickler">Entwickler</option>
              <option value="manager">Teamleitung</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <button onClick={addUser} style={{
            padding: '8px 16px', background: 'var(--brand-success)', color: '#fff',
            border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer',
            fontWeight: 600, fontFamily: 'var(--font-body)',
          }}>Anlegen</button>
          <button onClick={() => setShowAddUser(false)} style={{
            padding: '8px 16px', background: 'var(--brand-cream)', color: 'var(--brand-text-muted)',
            border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontSize: 12, cursor: 'pointer',
            fontFamily: 'var(--font-body)',
          }}>Abbrechen</button>
        </div>
      )}

      {/* User Table */}
      <div className="responsive-table-wrapper" style={{
        background: '#fff', borderRadius: 8, border: '1px solid var(--brand-warm-gray)', overflow: 'hidden',
      }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 600 }}>
          <thead>
            <tr style={{ background: 'var(--brand-cream)', borderBottom: '1px solid var(--brand-warm-gray)' }}>
              {['Name', 'E-Mail', 'Rolle', 'Letzter Login', 'Status', ''].map((h, i) => (
                <th key={i} style={{
                  padding: '10px 16px', textAlign: i === 5 ? 'right' : 'left',
                  fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid var(--brand-warm-gray)' }}>
                <td style={{ padding: '12px 16px', fontWeight: 500, color: 'var(--brand-navy)' }}>{u.name}</td>
                <td style={{ padding: '12px 16px', color: 'var(--brand-text-muted)' }}>{u.email}</td>
                <td style={{ padding: '12px 16px' }}>
                  <select
                    value={u.role}
                    onChange={e => changeRole(u.id, e.target.value)}
                    style={{
                      padding: '4px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                      border: 'none', fontFamily: 'var(--font-body)',
                      background: roleColors[u.role]?.bg || 'var(--brand-cream)',
                      color: roleColors[u.role]?.text || 'var(--brand-text-muted)',
                    }}
                  >
                    <option value="readonly">Readonly</option>
                    <option value="mitarbeiter">Mitarbeiter</option>
                    <option value="entwickler">Entwickler</option>
                    <option value="manager">Teamleitung</option>
                    <option value="admin">Admin</option>
                  </select>
                </td>
                <td style={{ padding: '12px 16px', color: 'var(--brand-text-muted)', fontSize: 12 }}>
                  {u.last_login ? new Date(u.last_login).toLocaleDateString('de-DE') : '—'}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <span style={{
                    padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 500,
                    background: u.is_active ? 'var(--brand-success-bg)' : 'var(--brand-danger-bg)',
                    color: u.is_active ? 'var(--brand-success)' : 'var(--brand-danger)',
                  }}>
                    {u.is_active ? 'Aktiv' : 'Deaktiviert'}
                  </span>
                </td>
                <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                  <button
                    onClick={() => toggleUser(u.id, u.is_active)}
                    style={{
                      padding: '4px 12px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                      background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                      color: 'var(--brand-text-muted)', fontFamily: 'var(--font-body)',
                      transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.target.style.borderColor = 'var(--brand-danger)'; e.target.style.color = 'var(--brand-danger)'; }}
                    onMouseLeave={e => { e.target.style.borderColor = 'var(--brand-warm-gray)'; e.target.style.color = 'var(--brand-text-muted)'; }}
                  >
                    {u.is_active ? 'Deaktivieren' : 'Aktivieren'}
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Benutzer "${u.name}" wirklich loeschen? Alle Vorgaenge werden dem Admin zugewiesen.`)) {
                        fetch(`${API_URL}/admin/users/${u.id}`, {
                          method: 'DELETE',
                          headers: { Authorization: `Bearer ${token}` },
                        }).then(r => {
                          if (r.ok) loadUsers();
                          else r.json().then(d => alert(d.detail || 'Fehler')).catch(() => alert('Fehler'));
                        });
                      }
                    }}
                    style={{
                      padding: '4px 12px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                      background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                      color: 'var(--brand-text-muted)', fontFamily: 'var(--font-body)',
                      marginLeft: 6, transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => { e.target.style.borderColor = 'var(--brand-danger)'; e.target.style.color = 'var(--brand-danger)'; }}
                    onMouseLeave={e => { e.target.style.borderColor = 'var(--brand-warm-gray)'; e.target.style.color = 'var(--brand-text-muted)'; }}
                  >Loeschen</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>
      </>)}

      {/* ══ Tab: Skill-Berechtigungen ══ */}
      {activeTab === 'berechtigungen' && (<>
        <div style={{ marginBottom: 8 }}>
          <p style={{ fontSize: 13, color: 'var(--brand-text-muted)', marginBottom: 16 }}>
            Lege fest welche Benutzergruppe welche Skills sehen und nutzen darf. Admin hat immer vollen Zugriff.
          </p>
        </div>
        {permLoading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--brand-text-muted)' }}>Laden...</div>
        ) : permData ? (
          <>
          <div style={{
            background: '#fff', borderRadius: 8, border: '1px solid var(--brand-warm-gray)',
            overflow: 'hidden', marginBottom: 16,
          }}>
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--brand-warm-gray)', background: 'var(--brand-cream)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)' }}>Developer Chat</div>
              <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginTop: 3 }}>
                Individuelle Freigabe. Standardmäßig aktiv für ts@fleet-immobilien.de und ps@fleet-immobilien.de.
              </div>
            </div>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 520 }}>
                <tbody>
                  {(permData.developer_chat_users || []).map(entry => (
                    <tr key={entry.user_id} style={{ borderBottom: '1px solid var(--brand-warm-gray)' }}>
                      <td style={{ padding: '10px 16px', fontWeight: 500, color: 'var(--brand-navy)' }}>{entry.name}</td>
                      <td style={{ padding: '10px 16px', color: 'var(--brand-text-muted)' }}>{entry.email}</td>
                      <td style={{ padding: '10px 16px', textAlign: 'right' }}>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', color: 'var(--brand-text-muted)' }}>
                          <input
                            type="checkbox"
                            checked={!!entry.can_view}
                            onChange={() => toggleDeveloperChatUser(entry)}
                            style={{ width: 16, height: 16 }}
                          />
                          <span>{entry.can_view ? 'Freigeschaltet' : 'Gesperrt'}</span>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div style={{
            background: '#fff', borderRadius: 8, border: '1px solid var(--brand-warm-gray)',
            overflow: 'auto',
          }}>
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 600 }}>
              <thead>
                <tr style={{ background: 'var(--brand-cream)', borderBottom: '2px solid var(--brand-warm-gray)' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px', minWidth: 140 }}>Skill</th>
                  {permData.roles.filter(r => r !== 'admin').map(role => (
                    <th key={role} colSpan={2} style={{ padding: '12px 8px', textAlign: 'center', fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', borderLeft: '1px solid var(--brand-warm-gray)' }}>
                      {roleLabels[role] || role}
                    </th>
                  ))}
                </tr>
                <tr style={{ background: 'var(--brand-cream)', borderBottom: '1px solid var(--brand-warm-gray)' }}>
                  <th></th>
                  {permData.roles.filter(r => r !== 'admin').map(role => (
                    <React.Fragment key={role}>
                      <th style={{ padding: '4px 8px', fontSize: 10, color: 'var(--brand-text-muted)', fontWeight: 500, borderLeft: '1px solid var(--brand-warm-gray)' }}>Sehen</th>
                      <th style={{ padding: '4px 8px', fontSize: 10, color: 'var(--brand-text-muted)', fontWeight: 500 }}>Nutzen</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {permData.skills.map(skill => (
                  <tr key={skill.id} style={{ borderBottom: '1px solid var(--brand-warm-gray)' }}>
                    <td style={{ padding: '10px 16px', fontWeight: 500, color: 'var(--brand-navy)' }}>
                      <span style={{ marginRight: 6 }}>{skill.icon}</span>{skill.name}
                    </td>
                    {permData.roles.filter(r => r !== 'admin').map(role => {
                      const p = permData.matrix[role]?.[skill.id] || { can_view: true, can_execute: true };
                      return (
                        <React.Fragment key={role}>
                          <td style={{ padding: '8px 8px', textAlign: 'center', borderLeft: '1px solid var(--brand-warm-gray)' }}>
                            <input type="checkbox" checked={p.can_view}
                              onChange={() => togglePerm(role, skill.id, 'can_view')}
                              style={{ cursor: 'pointer', width: 16, height: 16 }} />
                          </td>
                          <td style={{ padding: '8px 8px', textAlign: 'center' }}>
                            <input type="checkbox" checked={p.can_execute}
                              onChange={() => togglePerm(role, skill.id, 'can_execute')}
                              disabled={!p.can_view}
                              style={{ cursor: p.can_view ? 'pointer' : 'not-allowed', width: 16, height: 16, opacity: p.can_view ? 1 : 0.3 }} />
                          </td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table></div>
            <div style={{ padding: '10px 16px', background: 'var(--brand-cream)', borderTop: '1px solid var(--brand-warm-gray)', fontSize: 11, color: 'var(--brand-text-muted)' }}>
              Admin hat immer vollen Zugriff auf alle Skills. Änderungen werden sofort gespeichert.
            </div>
          </div>
          </>
        ) : (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--brand-text-muted)' }}>Fehler beim Laden</div>
        )}
      </>)}

      {/* ══ Tab: Objekt-IDs ══ */}
      {activeTab === 'objekte' && (<>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16,
      }}>
        <div>
          <h2 style={{
            fontFamily: 'var(--font-headline)', fontSize: 18, fontWeight: 400, color: 'var(--brand-navy)',
          }}>Objekt-IDs</h2>
          <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 2 }}>
            {objekte.filter(o => o.aktiv).length} aktiv von {objekte.length} Objekten — werden für den OP-Listen Monatsabruf verwendet
          </div>
        </div>
        <button
          onClick={() => setShowAddObjekt(true)}
          style={{
            padding: '8px 16px', fontSize: 12, fontWeight: 600,
            background: 'var(--brand-gold)', color: 'var(--brand-navy)',
            border: 'none', borderRadius: 4, cursor: 'pointer',
            textTransform: 'uppercase', letterSpacing: '0.5px',
            fontFamily: 'var(--font-body)',
          }}
        >
          + Objekt hinzufügen
        </button>
      </div>

      {/* Add Objekt */}
      {showAddObjekt && (
        <div style={{
          background: '#fff', borderRadius: 8, padding: 20,
          border: '1px solid var(--brand-gold)', marginBottom: 16,
          display: 'flex', gap: 12, alignItems: 'flex-end',
        }}>
          <div style={{ width: 120 }}>
            <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 4, color: 'var(--brand-navy)' }}>Objekt-Nr.</label>
            <input value={newObjekt.objekt_nr} onChange={e => setNewObjekt(p => ({ ...p, objekt_nr: e.target.value }))}
              placeholder="z.B. 225" style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ fontSize: 11, fontWeight: 500, display: 'block', marginBottom: 4, color: 'var(--brand-navy)' }}>Adresse (optional)</label>
            <input value={newObjekt.adresse} onChange={e => setNewObjekt(p => ({ ...p, adresse: e.target.value }))}
              placeholder="Straße, PLZ Ort" style={inputStyle} />
          </div>
          <button onClick={addObjekt} style={{
            padding: '8px 16px', background: 'var(--brand-success)', color: '#fff',
            border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer',
            fontWeight: 600, fontFamily: 'var(--font-body)',
          }}>Hinzufügen</button>
          <button onClick={() => setShowAddObjekt(false)} style={{
            padding: '8px 16px', background: 'var(--brand-cream)', color: 'var(--brand-text-muted)',
            border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontSize: 12, cursor: 'pointer',
            fontFamily: 'var(--font-body)',
          }}>Abbrechen</button>
        </div>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <input
          value={objektFilter}
          onChange={e => { setObjektFilter(e.target.value); setObjektPage(0); }}
          placeholder="Suchen nach Nr. oder Adresse..."
          style={{ ...inputStyle, maxWidth: 300 }}
        />
        <span style={{ fontSize: 12, color: 'var(--brand-text-muted)', whiteSpace: 'nowrap' }}>
          {filteredObjekte.length} Objekte
        </span>
      </div>

      {/* Objekt-Tabelle */}
      <div style={{
        background: '#fff', borderRadius: 8, border: '1px solid var(--brand-warm-gray)', overflow: 'hidden',
      }}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 600 }}>
          <thead>
            <tr style={{ background: 'var(--brand-cream)', borderBottom: '1px solid var(--brand-warm-gray)' }}>
              {['Nr.', 'Adresse', 'Verwaltungsart', 'Status', ''].map((h, i) => (
                <th key={i} style={{
                  padding: '10px 16px', textAlign: i === 4 ? 'right' : 'left',
                  fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)',
                  textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredObjekte.slice(objektPage * OBJEKTE_PER_PAGE, (objektPage + 1) * OBJEKTE_PER_PAGE).map(o => (
              <tr key={o.id} style={{
                borderBottom: '1px solid var(--brand-warm-gray)',
                opacity: o.aktiv ? 1 : 0.5,
              }}>
                {editObjekt?.id === o.id ? (<>
                  <td style={{ padding: '8px 16px' }}>
                    <input value={editObjekt.objekt_nr} disabled
                      style={{ ...inputStyle, width: 70, background: 'var(--brand-cream)', color: 'var(--brand-text-muted)' }} />
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <input value={editObjekt.adresse}
                      onChange={e => setEditObjekt(p => ({ ...p, adresse: e.target.value }))}
                      placeholder="Adresse eingeben..."
                      style={inputStyle}
                      autoFocus
                      onKeyDown={e => { if (e.key === 'Enter') saveObjekt(); if (e.key === 'Escape') setEditObjekt(null); }}
                    />
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <select value={editObjekt.verwaltungsart || ''}
                      onChange={e => setEditObjekt(p => ({ ...p, verwaltungsart: e.target.value }))}
                      style={{ ...inputStyle, background: '#fff', fontSize: 12 }}
                    >
                      <option value="">— Keine —</option>
                      <option value="Mietverwaltung">Mietverwaltung</option>
                      <option value="WEG-Verwaltung">WEG-Verwaltung</option>
                      <option value="WEG mit SE-Verwaltung">WEG mit SE-Verwaltung</option>
                    </select>
                  </td>
                  <td style={{ padding: '8px 16px' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 500,
                      background: o.aktiv ? 'var(--brand-success-bg)' : 'var(--brand-danger-bg)',
                      color: o.aktiv ? 'var(--brand-success)' : 'var(--brand-danger)',
                    }}>{o.aktiv ? 'Aktiv' : 'Inaktiv'}</span>
                  </td>
                  <td style={{ padding: '8px 16px', textAlign: 'right', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button onClick={saveObjekt} style={{
                      padding: '4px 12px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                      background: 'var(--brand-success)', border: 'none',
                      color: '#fff', fontFamily: 'var(--font-body)', fontWeight: 600,
                    }}>Speichern</button>
                    <button onClick={() => setEditObjekt(null)} style={{
                      padding: '4px 12px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                      background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                      color: 'var(--brand-text-muted)', fontFamily: 'var(--font-body)',
                    }}>Abbrechen</button>
                  </td>
                </>) : (<>
                  <td style={{ padding: '10px 16px', fontWeight: 600, color: 'var(--brand-navy)', fontFamily: 'var(--font-body)' }}>
                    {o.objekt_nr}
                  </td>
                  <td style={{ padding: '10px 16px', color: 'var(--brand-text-muted)' }}>
                    {o.adresse || '—'}
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{
                      padding: '3px 8px', borderRadius: 3, fontSize: 10, fontWeight: 500,
                      background: o.verwaltungsart === 'Mietverwaltung' ? 'var(--brand-gold-10)' :
                                 o.verwaltungsart === 'WEG-Verwaltung' ? 'var(--brand-info-bg)' :
                                 o.verwaltungsart === 'WEG mit SE-Verwaltung' ? 'var(--brand-cream)' : 'transparent',
                      color: o.verwaltungsart === 'Mietverwaltung' ? 'var(--brand-gold-dark)' :
                            o.verwaltungsart === 'WEG-Verwaltung' ? 'var(--brand-info)' :
                            o.verwaltungsart === 'WEG mit SE-Verwaltung' ? 'var(--brand-text-muted)' : 'var(--brand-text-light)',
                    }}>
                      {o.verwaltungsart || '—'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px' }}>
                    <span style={{
                      padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 500,
                      background: o.aktiv ? 'var(--brand-success-bg)' : 'var(--brand-danger-bg)',
                      color: o.aktiv ? 'var(--brand-success)' : 'var(--brand-danger)',
                      cursor: 'pointer',
                    }} onClick={() => toggleObjekt(o.id)}>
                      {o.aktiv ? 'Aktiv' : 'Inaktiv'}
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => setEditObjekt({ id: o.id, objekt_nr: o.objekt_nr, adresse: o.adresse || '', verwaltungsart: o.verwaltungsart || '' })}
                      style={{
                        padding: '4px 12px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                        background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                        color: 'var(--brand-text-muted)', fontFamily: 'var(--font-body)',
                      }}
                      onMouseEnter={e => { e.target.style.borderColor = 'var(--brand-gold)'; e.target.style.color = 'var(--brand-gold)'; }}
                      onMouseLeave={e => { e.target.style.borderColor = 'var(--brand-warm-gray)'; e.target.style.color = 'var(--brand-text-muted)'; }}
                    >Bearbeiten</button>
                    <button
                      onClick={() => { if (window.confirm(`Objekt ${o.objekt_nr} wirklich löschen?`)) deleteObjekt(o.id); }}
                      style={{
                        padding: '4px 12px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                        background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                        color: 'var(--brand-text-muted)', fontFamily: 'var(--font-body)',
                      }}
                      onMouseEnter={e => { e.target.style.borderColor = 'var(--brand-danger)'; e.target.style.color = 'var(--brand-danger)'; }}
                      onMouseLeave={e => { e.target.style.borderColor = 'var(--brand-warm-gray)'; e.target.style.color = 'var(--brand-text-muted)'; }}
                    >Löschen</button>
                  </td>
                </>)}
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {/* Pagination */}
      {filteredObjekte.length > OBJEKTE_PER_PAGE && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 4, marginTop: 12,
        }}>
          <button
            onClick={() => setObjektPage(p => Math.max(0, p - 1))}
            disabled={objektPage === 0}
            style={{
              padding: '8px 14px', fontSize: 12, borderRadius: 4, cursor: objektPage === 0 ? 'default' : 'pointer',
              background: 'transparent', border: '1px solid var(--brand-warm-gray)',
              color: objektPage === 0 ? 'var(--brand-text-light)' : 'var(--brand-text-muted)',
              fontFamily: 'var(--font-body)', opacity: objektPage === 0 ? 0.5 : 1,
            }}
          >Zurück</button>
          {Array.from({ length: Math.ceil(filteredObjekte.length / OBJEKTE_PER_PAGE) }, (_, i) => (
            <button key={i} onClick={() => setObjektPage(i)} style={{
              padding: '6px 10px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
              fontFamily: 'var(--font-body)', fontWeight: objektPage === i ? 700 : 400,
              background: objektPage === i ? 'var(--brand-gold)' : 'transparent',
              color: objektPage === i ? 'var(--brand-navy)' : 'var(--brand-text-muted)',
              border: objektPage === i ? 'none' : '1px solid var(--brand-warm-gray)',
              minWidth: 32,
            }}>{i + 1}</button>
          ))}
          <button
            onClick={() => setObjektPage(p => Math.min(Math.ceil(filteredObjekte.length / OBJEKTE_PER_PAGE) - 1, p + 1))}
            disabled={objektPage >= Math.ceil(filteredObjekte.length / OBJEKTE_PER_PAGE) - 1}
            style={{
              padding: '8px 14px', fontSize: 12, borderRadius: 4,
              cursor: objektPage >= Math.ceil(filteredObjekte.length / OBJEKTE_PER_PAGE) - 1 ? 'default' : 'pointer',
              background: 'transparent', border: '1px solid var(--brand-warm-gray)',
              color: objektPage >= Math.ceil(filteredObjekte.length / OBJEKTE_PER_PAGE) - 1 ? 'var(--brand-text-light)' : 'var(--brand-text-muted)',
              fontFamily: 'var(--font-body)',
              opacity: objektPage >= Math.ceil(filteredObjekte.length / OBJEKTE_PER_PAGE) - 1 ? 0.5 : 1,
            }}
          >Weiter</button>
        </div>
      )}
      </>)}

      {/* ══ Tab: Freigaben ══ */}
      {activeTab === 'freigaben' && (<>
        {freigabeStats && (
          <div className="stats-grid-3" style={{ marginBottom: 24 }}>
            {[
              { label: 'Offen', value: freigabeStats.angefragt, color: 'var(--brand-gold)' },
              { label: 'Genehmigt', value: freigabeStats.genehmigt, color: 'var(--brand-success)' },
              { label: 'Abgelehnt', value: freigabeStats.abgelehnt, color: 'var(--brand-danger)' },
            ].map(s => (
              <div key={s.label} style={{
                background: '#fff', borderRadius: 8, padding: '1rem 1.25rem',
                border: '1px solid var(--brand-warm-gray)', position: 'relative', overflow: 'hidden',
              }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: s.color }} />
                <div style={{
                  fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.8px',
                  color: 'var(--brand-text-muted)', fontWeight: 500, marginBottom: 6,
                }}>{s.label}</div>
                <div style={{ fontFamily: 'var(--font-headline)', fontSize: 28, color: 'var(--brand-navy)' }}>
                  {s.value}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{
          background: '#fff', borderRadius: 8, border: '1px solid var(--brand-warm-gray)', overflow: 'hidden',
        }}>
          {freigaben.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--brand-text-muted)', fontSize: 13 }}>
              Keine Freigaben vorhanden.
            </div>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 600 }}>
              <thead>
                <tr style={{ background: 'var(--brand-cream)', borderBottom: '1px solid var(--brand-warm-gray)' }}>
                  {['Datum', 'Benutzer', 'Typ', 'Vorgang', 'Begründung', 'Status', 'Aktion', ''].map((h, i) => (
                    <th key={i} style={{
                      padding: '10px 12px', textAlign: 'left',
                      fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)',
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {freigaben.map(f => {
                  const statusColors = { angefragt: 'var(--brand-gold)', genehmigt: 'var(--brand-success)', abgelehnt: 'var(--brand-danger)' };
                  const statusBg = { angefragt: 'var(--brand-gold-10)', genehmigt: 'var(--brand-success-bg)', abgelehnt: 'var(--brand-danger-bg)' };
                  const typeLabels = { mietvertrag_absage: 'MV Absage', signing_versand: 'Signatur' };
                  return (
                    <tr key={f.id} style={{ borderBottom: '1px solid var(--brand-warm-gray)' }}>
                      <td style={{ padding: '10px 12px', color: 'var(--brand-text-muted)', fontSize: 11, whiteSpace: 'nowrap' }}>
                        {f.created_at ? new Date(f.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--brand-navy)' }}>{f.requester_name}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          padding: '3px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                          background: 'var(--brand-danger-bg)', color: 'var(--brand-danger)',
                          textTransform: 'uppercase', letterSpacing: '0.3px',
                        }}>{typeLabels[f.resource_type] || f.resource_type}</span>
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--brand-text)', fontSize: 12, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <span onClick={() => loadFreigabeDetail(f)} style={{ cursor: 'pointer', color: 'var(--brand-gold)', fontWeight: 500, textDecoration: 'underline' }}>
                          {f.beschreibung || `#${f.resource_id}`}
                        </span>
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--brand-text-muted)', fontSize: 12, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.grund || '—'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 500,
                          background: statusBg[f.status], color: statusColors[f.status],
                        }}>{f.status === 'angefragt' ? 'Offen' : f.status === 'genehmigt' ? 'Genehmigt' : 'Abgelehnt'}</span>
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {f.status === 'angefragt' ? (
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => handleFreigabeDecision(f.id, 'genehmigt')} style={{
                              padding: '4px 12px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                              background: 'var(--brand-success)', border: 'none',
                              color: '#fff', fontFamily: 'var(--font-body)', fontWeight: 600,
                            }}>Genehmigen</button>
                            <button onClick={() => handleFreigabeDecision(f.id, 'abgelehnt')} style={{
                              padding: '4px 12px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                              background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                              color: 'var(--brand-text-muted)', fontFamily: 'var(--font-body)',
                            }}
                              onMouseEnter={e => { e.target.style.borderColor = 'var(--brand-danger)'; e.target.style.color = 'var(--brand-danger)'; }}
                              onMouseLeave={e => { e.target.style.borderColor = 'var(--brand-warm-gray)'; e.target.style.color = 'var(--brand-text-muted)'; }}
                            >Ablehnen</button>
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: 'var(--brand-text-light)' }}>
                            {f.approver_name || '—'}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                        <button onClick={() => handleFreigabeDelete(f.id)} title="Eintrag loeschen" style={{
                          padding: '3px 8px', fontSize: 13, borderRadius: 4, cursor: 'pointer',
                          background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                          color: 'var(--brand-text-light)', fontFamily: 'var(--font-body)', lineHeight: 1,
                        }}
                          onMouseEnter={e => { e.target.style.borderColor = 'var(--brand-danger)'; e.target.style.color = 'var(--brand-danger)'; }}
                          onMouseLeave={e => { e.target.style.borderColor = 'var(--brand-warm-gray)'; e.target.style.color = 'var(--brand-text-light)'; }}
                        >&times;</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></div>
          )}
        </div>

        {/* Freigabe-Detailansicht */}
        {freigabeDetailLoading && (
          <div style={{ marginTop: 16, padding: 20, background: '#fff', borderRadius: 8, border: '1px solid var(--brand-warm-gray)', textAlign: 'center', color: 'var(--brand-text-muted)', fontSize: 13 }}>
            Vorgang wird geladen...
          </div>
        )}
        {freigabeDetail && !freigabeDetailLoading && (() => {
          const { freigabe: fg, execution: ex } = freigabeDetail;
          const result = ex.result || {};
          const extracted = result.extracted_data || {};
          const scoring = result.scoring || extracted.scoring || {};
          const signing = ex.signing;

          const editField = (key, label, type) => (
            <div key={key} style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)', display: 'block', marginBottom: 2 }}>{label}</label>
              <input value={detailEditData[key] || ''} onChange={e => setDetailEditData(prev => ({ ...prev, [key]: e.target.value }))}
                type={type || 'text'}
                style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontSize: 12, fontFamily: 'var(--font-body)' }}
              />
            </div>
          );

          return (
            <div style={{
              marginTop: 16, background: '#fff', borderRadius: 8,
              border: '1px solid var(--brand-gold)', overflow: 'hidden',
            }}>
              <div style={{
                padding: '12px 20px', background: 'var(--brand-navy)', color: 'var(--brand-gold)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Vorgang #{ex.id} — {ex.summary || 'Mietvertragspruefung'}
                </div>
                <button onClick={() => setFreigabeDetail(null)} style={{
                  background: 'transparent', border: 'none', color: 'var(--brand-gold)',
                  cursor: 'pointer', fontSize: 16, fontWeight: 700,
                }}>x</button>
              </div>
              <div style={{ padding: 20 }}>
                {/* Meta-Info */}
                <div className="stats-grid-3" style={{ marginBottom: 16, fontSize: 12 }}>
                  <div><span style={{ color: 'var(--brand-text-muted)' }}>Erstellt von:</span><br/><strong>{ex.owner_name || '—'}</strong></div>
                  <div><span style={{ color: 'var(--brand-text-muted)' }}>Datum:</span><br/><strong>{ex.created_at ? new Date(ex.created_at).toLocaleString('de-DE') : '—'}</strong></div>
                  <div><span style={{ color: 'var(--brand-text-muted)' }}>Phase:</span><br/><strong>{result.phase === 'documents_created' ? 'Dokumente erstellt' : result.phase === 'analyzed' ? 'Analysiert' : result.phase || '—'}</strong></div>
                </div>

                {/* Scoring */}
                {scoring.gesamt != null && (
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
                    padding: '10px 14px', borderRadius: 6,
                    background: scoring.gesamt >= 75 ? 'var(--brand-success-bg)' : scoring.gesamt >= 50 ? 'var(--brand-warning-bg)' : 'var(--brand-danger-bg)',
                    border: `1px solid ${scoring.gesamt >= 75 ? 'var(--brand-success)' : scoring.gesamt >= 50 ? 'var(--brand-warning)' : 'var(--brand-danger)'}`,
                  }}>
                    <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'var(--font-headline)' }}>{scoring.gesamt}/100</div>
                    <div style={{ fontSize: 12 }}>
                      <div style={{ fontWeight: 600 }}>{(extracted.empfehlung || '').replace(/_/g, ' ')}</div>
                      <div style={{ color: 'var(--brand-text-muted)' }}>Bonitaet {scoring.bonitaet}/25 | Einkommen {scoring.einkommen}/25 | Beschaeftigung {scoring.beschaeftigung}/20</div>
                    </div>
                  </div>
                )}

                {/* Daten: Ansicht oder Bearbeitung */}
                {!detailEditMode ? (
                  <div style={{
                    padding: 14, background: 'var(--brand-cream)', borderRadius: 6, fontSize: 12, marginBottom: 16,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                      <div style={{ fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.5px' }}>Vertragsdaten</div>
                      <button onClick={() => setDetailEditMode(true)} style={{
                        padding: '4px 12px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                        background: 'transparent', border: '1px solid var(--brand-gold)',
                        color: 'var(--brand-gold)', fontFamily: 'var(--font-body)', fontWeight: 600,
                      }}>Bearbeiten</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      {[
                        ['Objekt', extracted.objektanschrift],
                        ['Einheit', extracted.einheitsnummer],
                        ['Eigentuemer', extracted.eigentuemer],
                        ['Eigentuemer Anschrift', extracted.eigentuemer_anschrift],
                        ['Mieter', extracted.mieter_name],
                        ['Mieter 2', extracted.mieter2_name],
                        ['Geburtsdatum', extracted.mieter_geburtsdatum],
                        ['Anschrift Mieter', extracted.mieter_anschrift],
                        ['Mietbeginn', extracted.mietbeginn],
                        ['Kaltmiete', extracted.kaltmiete ? `${extracted.kaltmiete} EUR` : ''],
                        ['Gesamtmiete', extracted.gesamtmiete ? `${extracted.gesamtmiete} EUR` : ''],
                        ['Kaution', extracted.kaution ? `${extracted.kaution} EUR` : ''],
                        ['Mietkonto IBAN', extracted.mietkonto_iban],
                        ['Mietkonto BIC', extracted.mietkonto_bic],
                        ['SCHUFA', extracted.schufa_score],
                        ['Arbeitgeber', extracted.arbeitgeber],
                        ['Nettoeinkommen', extracted.nettoeinkommen ? `${extracted.nettoeinkommen} EUR` : ''],
                        ['Beschaeftigung', extracted.beschaeftigung],
                      ].filter(([, v]) => v).map(([label, value]) => (
                        <div key={label}>
                          <span style={{ color: 'var(--brand-text-muted)' }}>{label}:</span>{' '}
                          <strong>{value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div style={{
                    padding: 14, background: '#fff', borderRadius: 6, border: '1px solid var(--brand-gold)',
                    marginBottom: 16,
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-gold)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>
                      Daten bearbeiten
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                      {editField('eigentuemer', 'Eigentuemer')}
                      {editField('eigentuemer_anschrift', 'Eigentuemer Anschrift')}
                      {editField('mieter_name', 'Mieter Name')}
                      {editField('mieter2_name', 'Mieter 2 Name')}
                      {editField('mieter_anschrift', 'Mieter Anschrift')}
                      {editField('mieter_geburtsdatum', 'Geburtsdatum')}
                      {editField('objektanschrift', 'Objektanschrift')}
                      {editField('einheitsnummer', 'Einheitsnummer')}
                      {editField('mietbeginn', 'Mietbeginn')}
                      {editField('kaltmiete', 'Kaltmiete (EUR)')}
                      {editField('gesamtmiete', 'Gesamtmiete (EUR)')}
                      {editField('kaution', 'Kaution (EUR)')}
                      {editField('mietkonto_iban', 'Mietkonto IBAN')}
                      {editField('mietkonto_bic', 'Mietkonto BIC')}
                      {editField('mietkonto_inhaber', 'Mietkonto Inhaber')}
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      <button onClick={saveDetailEdit} style={{
                        padding: '8px 20px', fontSize: 12, fontWeight: 600, borderRadius: 4,
                        background: 'var(--brand-gold)', color: 'var(--brand-navy)',
                        border: 'none', cursor: 'pointer', fontFamily: 'var(--font-body)',
                      }}>Speichern</button>
                      <button onClick={() => { setDetailEditMode(false); setDetailEditData(extracted); }} style={{
                        padding: '8px 20px', fontSize: 12, borderRadius: 4,
                        background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                        color: 'var(--brand-text-muted)', cursor: 'pointer', fontFamily: 'var(--font-body)',
                      }}>Abbrechen</button>
                    </div>
                  </div>
                )}

                {/* Dokumente */}
                {ex.output_files?.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Generierte Dokumente</div>
                    {ex.output_files.map((f, i) => (
                      <a key={i} href={`/outputs/${f}?token=${token}`} download style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 12px', marginBottom: 4,
                        background: 'var(--brand-cream)', borderRadius: 4,
                        color: 'var(--brand-gold)', fontSize: 12, fontWeight: 500,
                        textDecoration: 'none',
                      }}>
                        <span style={{ fontSize: 10, padding: '1px 6px', background: 'var(--brand-navy)', color: '#fff', borderRadius: 2, fontWeight: 600, textTransform: 'uppercase' }}>
                          {f.split('.').pop()}
                        </span>
                        {f.split('/').pop()}
                      </a>
                    ))}
                  </div>
                )}

                {/* Signatur-Dokumente (Mietvertrag + Anlagen PDF) */}
                {signing?.files?.length > 0 && (
                  <div style={{ marginBottom: 16 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Signatur-Dokumente (finale PDFs)</div>
                    {signing.files.map((f, i) => (
                      <div key={i} style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '8px 12px', marginBottom: 4,
                        background: 'var(--brand-cream)', borderRadius: 4,
                      }}>
                        <span style={{ fontSize: 10, padding: '1px 6px', background: f.type === 'mietvertrag' ? 'var(--brand-gold)' : 'var(--brand-navy)', color: f.type === 'mietvertrag' ? 'var(--brand-navy)' : '#fff', borderRadius: 2, fontWeight: 600, textTransform: 'uppercase' }}>
                          {f.type}
                        </span>
                        <span style={{ flex: 1, fontSize: 12, color: 'var(--brand-navy)' }}>{f.filename}</span>
                        <button onClick={async () => {
                          try {
                            const res = await fetch(`${API_URL}/signing/file/${ex.id}/${i}`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (!res.ok) return;
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            window.open(url, '_blank');
                          } catch (err) {}
                        }} style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-gold)', background: 'transparent', cursor: 'pointer', padding: '3px 10px', border: '1px solid var(--brand-gold)', borderRadius: 3, fontFamily: 'var(--font-body)' }}
                        >Vorschau</button>
                        <button onClick={async () => {
                          try {
                            const res = await fetch(`${API_URL}/signing/file/${ex.id}/${i}`, {
                              headers: { Authorization: `Bearer ${token}` },
                            });
                            if (!res.ok) return;
                            const blob = await res.blob();
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url; a.download = f.filename; a.click();
                            URL.revokeObjectURL(url);
                          } catch (err) {}
                        }} style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', background: 'transparent', cursor: 'pointer', padding: '3px 10px', border: '1px solid var(--brand-warm-gray)', borderRadius: 3, fontFamily: 'var(--font-body)' }}
                        >Download</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Signatur-Status */}
                {signing && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 6, marginBottom: 16,
                    background: signing.status === 'sent' ? 'var(--brand-success-bg)' : signing.status === 'uploaded' ? 'var(--brand-cream)' : 'var(--brand-cream)',
                    border: `1px solid ${signing.status === 'sent' ? 'var(--brand-success)' : 'var(--brand-warm-gray)'}`,
                    fontSize: 12,
                  }}>
                    <div style={{ fontWeight: 600, marginBottom: 4 }}>Digitale Signatur ({(signing.level || 'qes').toUpperCase()})</div>
                    <div>Status: <strong>{
                      signing.request_status === 'SIGNED' ? 'Vollstaendig unterschrieben' :
                      signing.status === 'sent' ? 'Versendet — warte auf Unterschriften' :
                      signing.status === 'uploaded' ? 'Dokumente hochgeladen — noch nicht versendet' :
                      signing.request_status || signing.status
                    }</strong></div>
                  </div>
                )}

                {/* Pruefbericht */}
                {result.report && (
                  <div style={{ marginBottom: 16 }}>
                    <button onClick={() => setDetailShowReport(!detailShowReport)} style={{
                      padding: '8px 14px', fontSize: 12, fontWeight: 600,
                      background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                      borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-body)',
                      color: 'var(--brand-navy)', width: '100%', textAlign: 'left',
                    }}>
                      {detailShowReport ? '— Pruefbericht ausblenden' : '+ Pruefbericht anzeigen'}
                    </button>
                    {detailShowReport && (
                      <div style={{
                        marginTop: 8, padding: 16, border: '1px solid var(--brand-warm-gray)',
                        borderRadius: 6, fontSize: 12, maxHeight: 500, overflow: 'auto',
                      }}>
                        <style>{`
                          .admin-report table { width: 100%; border-collapse: collapse; margin: 8px 0; font-size: 12px; }
                          .admin-report th { background: var(--brand-cream); padding: 6px 10px; text-align: left; font-weight: 600; border: 1px solid var(--brand-warm-gray); font-size: 11px; }
                          .admin-report td { padding: 6px 10px; border: 1px solid var(--brand-warm-gray); }
                          .admin-report h3 { font-size: 15px; color: var(--brand-navy); margin: 16px 0 6px; }
                          .admin-report h4 { font-size: 13px; margin: 12px 0 4px; }
                          .admin-report ul { margin: 4px 0; padding-left: 18px; }
                        `}</style>
                        <div className="admin-report">
                          <ReactMarkdown>{result.report}</ReactMarkdown>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Freigabe-Grund */}
                {fg.grund && (
                  <div style={{
                    padding: '10px 14px', borderRadius: 6, marginBottom: 16,
                    background: '#FFF8E7', border: '1px solid #F0D060', fontSize: 12,
                  }}>
                    <div style={{ fontWeight: 600, color: '#8B6914', marginBottom: 4 }}>Begruendung des Mitarbeiters</div>
                    <div style={{ color: '#5C4610' }}>{fg.grund}</div>
                  </div>
                )}

                {/* Aktionen */}
                {fg.status === 'angefragt' && (
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button onClick={() => handleFreigabeDecision(fg.id, 'genehmigt')} style={{
                      flex: 1, padding: '12px 20px', fontSize: 13, fontWeight: 600,
                      background: 'var(--brand-success)', border: 'none', borderRadius: 4,
                      color: '#fff', cursor: 'pointer', fontFamily: 'var(--font-body)',
                      textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>Genehmigen</button>
                    <button onClick={() => handleFreigabeDecision(fg.id, 'abgelehnt')} style={{
                      flex: 1, padding: '12px 20px', fontSize: 13, fontWeight: 600,
                      background: 'transparent', border: '1px solid var(--brand-danger)',
                      borderRadius: 4, color: 'var(--brand-danger)', cursor: 'pointer',
                      fontFamily: 'var(--font-body)', textTransform: 'uppercase', letterSpacing: '0.5px',
                    }}>Ablehnen</button>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </>)}

      {/* ══ Tab: Tickets ══ */}
      {activeTab === 'tickets' && (<>
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        marginBottom: 16,
      }}>
        <div>
          <h2 style={{
            fontFamily: 'var(--font-headline)', fontSize: 18, fontWeight: 400, color: 'var(--brand-navy)',
          }}>Feedback-Tickets</h2>
          {ticketStats && (
            <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 2 }}>
              {ticketStats.offen} offen &middot; {ticketStats.in_bearbeitung} in Bearbeitung &middot; {ticketStats.erledigt} erledigt
            </div>
          )}
        </div>
      </div>

      {/* Ticket Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        {[
          { value: '', label: 'Alle' },
          { value: 'offen', label: 'Offen' },
          { value: 'in_bearbeitung', label: 'In Bearbeitung' },
          { value: 'erledigt', label: 'Erledigt' },
        ].map(f => (
          <button key={f.value} onClick={() => setTicketStatusFilter(f.value)} style={{
            padding: '6px 14px', fontSize: 11, fontWeight: 500, borderRadius: 4,
            cursor: 'pointer', fontFamily: 'var(--font-body)',
            border: ticketStatusFilter === f.value ? '1.5px solid var(--brand-gold)' : '1px solid var(--brand-warm-gray)',
            background: ticketStatusFilter === f.value ? 'var(--brand-gold-10)' : '#fff',
            color: ticketStatusFilter === f.value ? 'var(--brand-gold-dark, var(--brand-gold))' : 'var(--brand-text-muted)',
          }}>{f.label}</button>
        ))}
        <input
          value={ticketFilter}
          onChange={e => setTicketFilter(e.target.value)}
          placeholder="Suchen..."
          style={{ ...inputStyle, maxWidth: 200, marginLeft: 'auto' }}
        />
      </div>

      {/* Ticket-Tabelle */}
      <div style={{
        background: '#fff', borderRadius: 8, border: '1px solid var(--brand-warm-gray)', overflow: 'hidden',
      }}>
        {tickets.filter(t =>
          !ticketFilter || t.betreff.toLowerCase().includes(ticketFilter.toLowerCase()) || (t.user_name || '').toLowerCase().includes(ticketFilter.toLowerCase())
        ).length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--brand-text-muted)', fontSize: 13 }}>
            Keine Tickets vorhanden.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 600 }}>
            <thead>
              <tr style={{ background: 'var(--brand-cream)', borderBottom: '1px solid var(--brand-warm-gray)' }}>
                {['#', 'Kategorie', 'Betreff', 'Von', 'Seite', 'Dateien', 'Status', 'Datum', ''].map((h, i) => (
                  <th key={i} style={{
                    padding: '10px 12px', textAlign: i === 8 ? 'right' : 'left',
                    fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)',
                    textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tickets.filter(t =>
                !ticketFilter || t.betreff.toLowerCase().includes(ticketFilter.toLowerCase()) || (t.user_name || '').toLowerCase().includes(ticketFilter.toLowerCase())
              ).map(t => {
                const katColors = { bug: 'var(--brand-danger)', verbesserung: 'var(--brand-info)', frage: 'var(--brand-gold)', sonstiges: 'var(--brand-text-muted)' };
                const katBg = { bug: 'var(--brand-danger-bg)', verbesserung: 'var(--brand-info-bg)', frage: 'var(--brand-gold-10)', sonstiges: 'var(--brand-cream)' };
                const statusColors = { offen: 'var(--brand-danger)', in_bearbeitung: 'var(--brand-warning, var(--brand-gold))', erledigt: 'var(--brand-success)' };
                const statusBg = { offen: 'var(--brand-danger-bg)', in_bearbeitung: 'var(--brand-gold-10)', erledigt: 'var(--brand-success-bg)' };
                const statusLabels = { offen: 'Offen', in_bearbeitung: 'In Bearbeitung', erledigt: 'Erledigt' };
                const isExpanded = expandedTicket === t.id;

                return (
                  <React.Fragment key={t.id}>
                    <tr style={{
                      borderBottom: isExpanded ? 'none' : '1px solid var(--brand-warm-gray)',
                      cursor: 'pointer', transition: 'background 0.1s',
                    }}
                      onClick={() => { setExpandedTicket(isExpanded ? null : t.id); setEditNotiz(t.admin_notiz || ''); }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--brand-cream)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                    >
                      <td style={{ padding: '10px 12px', color: 'var(--brand-text-muted)', fontSize: 11, fontWeight: 600 }}>{t.id}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          padding: '3px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600,
                          background: katBg[t.kategorie] || 'var(--brand-cream)',
                          color: katColors[t.kategorie] || 'var(--brand-text-muted)',
                          textTransform: 'uppercase', letterSpacing: '0.3px',
                        }}>{t.kategorie}</span>
                      </td>
                      <td style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--brand-navy)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {t.betreff}
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--brand-text-muted)', fontSize: 12 }}>{t.user_name || '—'}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--brand-text-light)', fontSize: 11 }}>{t.seite || '—'}</td>
                      <td style={{ padding: '10px 12px', color: 'var(--brand-text-muted)', fontSize: 11 }}>
                        {t.anhang_count > 0 ? `${t.anhang_count} Datei${t.anhang_count > 1 ? 'en' : ''}` : '—'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 500,
                          background: statusBg[t.status], color: statusColors[t.status],
                        }}>{statusLabels[t.status] || t.status}</span>
                      </td>
                      <td style={{ padding: '10px 12px', color: 'var(--brand-text-muted)', fontSize: 11, whiteSpace: 'nowrap' }}>
                        {t.created_at ? new Date(t.created_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                        <span style={{ fontSize: 14, color: 'var(--brand-text-light)' }}>{isExpanded ? '\u25B2' : '\u25BC'}</span>
                      </td>
                    </tr>

                    {/* Expanded Detail */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} style={{ padding: 0, borderBottom: '1px solid var(--brand-warm-gray)' }}>
                          <div style={{ padding: '16px 20px', background: 'var(--brand-cream)' }}>
                            {/* Meta-Info: Seite + Melder */}
                            <div style={{
                              display: 'flex', gap: 16, marginBottom: 14, padding: '8px 12px',
                              background: '#fff', borderRadius: 6, border: '1px solid var(--brand-warm-gray)',
                            }}>
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Gemeldet von</div>
                                <div style={{ fontSize: 13, color: 'var(--brand-navy)', fontWeight: 500, marginTop: 2 }}>{t.user_name || '—'}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Seite</div>
                                <div style={{ fontSize: 13, color: 'var(--brand-navy)', fontWeight: 500, marginTop: 2, fontFamily: 'monospace' }}>{t.seite || '—'}</div>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Datum</div>
                                <div style={{ fontSize: 13, color: 'var(--brand-navy)', marginTop: 2 }}>
                                  {t.created_at ? new Date(t.created_at).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                                </div>
                              </div>
                            </div>

                            {/* Beschreibung */}
                            <div style={{ marginBottom: 14 }}>
                              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>Beschreibung</div>
                              <div style={{ fontSize: 13, color: 'var(--brand-text)', lineHeight: 1.5, whiteSpace: 'pre-wrap', background: '#fff', padding: '10px 12px', borderRadius: 6, border: '1px solid var(--brand-warm-gray)' }}>{t.beschreibung}</div>
                            </div>

                            {/* Anhang-Dateien mit Vorschau */}
                            {t.anhang_dateien && t.anhang_dateien.length > 0 && (
                              <div style={{ marginBottom: 14 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Anhaenge ({t.anhang_dateien.length})</div>
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  {t.anhang_dateien.map((name, i) => {
                                    const displayName = name.includes('_') ? name.split('_').slice(1).join('_') : name;
                                    const ext = displayName.split('.').pop().toLowerCase();
                                    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext);
                                    const isPdf = ext === 'pdf';
                                    const openFile = async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const res = await fetch(`${API_URL}/feedback/tickets/${t.id}/files/${name}`, {
                                          headers: { Authorization: `Bearer ${token}` },
                                        });
                                        if (!res.ok) return;
                                        const blob = await res.blob();
                                        const url = URL.createObjectURL(blob);
                                        window.open(url, '_blank');
                                      } catch {}
                                    };
                                    const downloadFile = async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const res = await fetch(`${API_URL}/feedback/tickets/${t.id}/files/${name}`, {
                                          headers: { Authorization: `Bearer ${token}` },
                                        });
                                        if (!res.ok) return;
                                        const blob = await res.blob();
                                        const url = URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url; a.download = displayName; a.click();
                                        URL.revokeObjectURL(url);
                                      } catch {}
                                    };
                                    return (
                                      <div key={i} style={{
                                        background: '#fff', borderRadius: 6, border: '1px solid var(--brand-warm-gray)',
                                        overflow: 'hidden', width: isImage ? 140 : 'auto',
                                      }}>
                                        {isImage && (
                                          <div
                                            onClick={openFile}
                                            style={{ cursor: 'pointer', height: 90, overflow: 'hidden', background: 'var(--brand-cream)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                          >
                                            <img
                                              src={`${API_URL}/feedback/tickets/${t.id}/files/${name}`}
                                              alt={displayName}
                                              style={{ maxWidth: '100%', maxHeight: 90, objectFit: 'cover' }}
                                              onError={e => { e.target.style.display = 'none'; }}
                                            />
                                          </div>
                                        )}
                                        <div style={{
                                          padding: '5px 8px', display: 'flex', alignItems: 'center', gap: 6,
                                          borderTop: isImage ? '1px solid var(--brand-warm-gray)' : 'none',
                                        }}>
                                          <span style={{
                                            padding: '1px 4px', borderRadius: 2, fontSize: 9, fontWeight: 700,
                                            background: isPdf ? '#E74C3C' : isImage ? 'var(--brand-info)' : 'var(--brand-text-muted)',
                                            color: '#fff', textTransform: 'uppercase', flexShrink: 0,
                                          }}>{ext}</span>
                                          <span style={{ fontSize: 10, color: 'var(--brand-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                                            {displayName}
                                          </span>
                                          <button onClick={openFile} title="Vorschau" style={{
                                            background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', flexShrink: 0, color: 'var(--brand-gold)',
                                          }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                                          </button>
                                          <button onClick={downloadFile} title="Herunterladen" style={{
                                            background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px', flexShrink: 0, color: 'var(--brand-text-muted)',
                                          }}>
                                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Admin-Aktionen */}
                            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                              {/* Status */}
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 4 }}>Status</div>
                                <select
                                  value={t.status}
                                  onClick={e => e.stopPropagation()}
                                  onChange={async e => {
                                    e.stopPropagation();
                                    await fetch(`${API_URL}/feedback/tickets/${t.id}`, {
                                      method: 'PUT',
                                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ status: e.target.value }),
                                    });
                                    loadTickets(); loadTicketStats();
                                  }}
                                  style={{
                                    padding: '6px 10px', borderRadius: 4, fontSize: 12, border: '1px solid var(--brand-warm-gray)',
                                    fontFamily: 'var(--font-body)', background: '#fff', cursor: 'pointer',
                                  }}
                                >
                                  <option value="offen">Offen</option>
                                  <option value="in_bearbeitung">In Bearbeitung</option>
                                  <option value="erledigt">Erledigt</option>
                                </select>
                              </div>

                              {/* Prioritaet */}
                              <div>
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 4 }}>Prioritaet</div>
                                <select
                                  value={t.prioritaet}
                                  onClick={e => e.stopPropagation()}
                                  onChange={async e => {
                                    e.stopPropagation();
                                    await fetch(`${API_URL}/feedback/tickets/${t.id}`, {
                                      method: 'PUT',
                                      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ prioritaet: e.target.value }),
                                    });
                                    loadTickets();
                                  }}
                                  style={{
                                    padding: '6px 10px', borderRadius: 4, fontSize: 12, border: '1px solid var(--brand-warm-gray)',
                                    fontFamily: 'var(--font-body)', background: '#fff', cursor: 'pointer',
                                  }}
                                >
                                  <option value="niedrig">Niedrig</option>
                                  <option value="normal">Normal</option>
                                  <option value="hoch">Hoch</option>
                                </select>
                              </div>

                              {/* Notiz */}
                              <div style={{ flex: 1, minWidth: 200 }}>
                                <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 4 }}>Admin-Notiz</div>
                                <div style={{ display: 'flex', gap: 6 }}>
                                  <input
                                    value={editNotiz}
                                    onClick={e => e.stopPropagation()}
                                    onChange={e => setEditNotiz(e.target.value)}
                                    placeholder="Notiz hinzufuegen..."
                                    style={{ ...inputStyle, flex: 1 }}
                                    onKeyDown={async e => {
                                      if (e.key === 'Enter') {
                                        await fetch(`${API_URL}/feedback/tickets/${t.id}`, {
                                          method: 'PUT',
                                          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ admin_notiz: editNotiz }),
                                        });
                                        loadTickets();
                                      }
                                    }}
                                  />
                                  <button
                                    onClick={async e => {
                                      e.stopPropagation();
                                      await fetch(`${API_URL}/feedback/tickets/${t.id}`, {
                                        method: 'PUT',
                                        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ admin_notiz: editNotiz }),
                                      });
                                      loadTickets();
                                    }}
                                    style={{
                                      padding: '8px 14px', background: 'var(--brand-success)', color: '#fff',
                                      border: 'none', borderRadius: 4, fontSize: 11, cursor: 'pointer',
                                      fontWeight: 600, fontFamily: 'var(--font-body)', whiteSpace: 'nowrap',
                                    }}
                                  >Speichern</button>
                                </div>
                              </div>

                              {/* Loeschen */}
                              <button
                                onClick={async e => {
                                  e.stopPropagation();
                                  if (window.confirm(`Ticket #${t.id} wirklich loeschen?`)) {
                                    await fetch(`${API_URL}/feedback/tickets/${t.id}`, {
                                      method: 'DELETE',
                                      headers: { Authorization: `Bearer ${token}` },
                                    });
                                    setExpandedTicket(null);
                                    loadTickets(); loadTicketStats();
                                  }
                                }}
                                style={{
                                  padding: '8px 14px', fontSize: 11, borderRadius: 4, cursor: 'pointer',
                                  background: 'transparent', border: '1px solid var(--brand-warm-gray)',
                                  color: 'var(--brand-text-muted)', fontFamily: 'var(--font-body)',
                                  alignSelf: 'flex-end',
                                }}
                                onMouseEnter={e => { e.target.style.borderColor = 'var(--brand-danger)'; e.target.style.color = 'var(--brand-danger)'; }}
                                onMouseLeave={e => { e.target.style.borderColor = 'var(--brand-warm-gray)'; e.target.style.color = 'var(--brand-text-muted)'; }}
                              >Loeschen</button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table></div>
        )}
      </div>
      </>)}

      {activeTab === 'ki-kosten' && (<>
        {aiCostsLoading ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--brand-text-muted)', fontSize: 13 }}>Wird geladen...</div>
        ) : aiCosts ? (
          <div>
            {/* Monatsauswahl */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <select
                value={aiCostsMonth || aiCosts.month}
                onChange={e => { setAiCostsMonth(e.target.value); loadAiCosts(e.target.value); }}
                style={{ padding: '6px 12px', fontSize: 13, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, background: '#fff', color: 'var(--brand-navy)' }}
              >
                {(aiCosts.available_months?.length > 0 ? aiCosts.available_months : [aiCosts.month]).map(m => (
                  <option key={m} value={m}>{new Date(m + '-01').toLocaleDateString('de-DE', { month: 'long', year: 'numeric' })}</option>
                ))}
              </select>
            </div>

            {/* Gesamtkosten-Karten */}
            <div className="stats-grid-4" style={{ marginBottom: 20 }}>
              <div style={{ background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' }}>
                <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Gesamtkosten</div>
                <div style={{ fontFamily: 'var(--font-headline)', fontSize: 28, color: 'var(--brand-navy)' }}>${aiCosts.total?.cost_usd?.toFixed(2)}</div>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>~{aiCosts.total?.cost_eur?.toFixed(2)} EUR</div>
              </div>
              <div style={{ background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' }}>
                <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>API-Aufrufe</div>
                <div style={{ fontFamily: 'var(--font-headline)', fontSize: 28, color: 'var(--brand-navy)' }}>{aiCosts.total?.calls}</div>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>in diesem Monat</div>
              </div>
              <div style={{ background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' }}>
                <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Input-Tokens</div>
                <div style={{ fontFamily: 'var(--font-headline)', fontSize: 28, color: 'var(--brand-navy)' }}>{(aiCosts.total?.input_tokens || 0).toLocaleString('de-DE')}</div>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>$0.80 / 1M Tokens</div>
              </div>
              <div style={{ background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' }}>
                <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 6 }}>Output-Tokens</div>
                <div style={{ fontFamily: 'var(--font-headline)', fontSize: 28, color: 'var(--brand-navy)' }}>{(aiCosts.total?.output_tokens || 0).toLocaleString('de-DE')}</div>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 4 }}>$4.00 / 1M Tokens</div>
              </div>
            </div>

            <div className="stats-grid-2" style={{ marginBottom: 20 }}>
              {/* Kosten nach Feature */}
              <div style={{ background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Nach Feature</div>
                {Object.entries(aiCosts.by_feature || {}).map(([feature, data]) => (
                  <div key={feature} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--brand-warm-gray)' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)', textTransform: 'capitalize' }}>{feature}</div>
                      <div style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>{data.calls} Aufrufe</div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)' }}>${data.cost_usd?.toFixed(4)}</div>
                  </div>
                ))}
                {Object.keys(aiCosts.by_feature || {}).length === 0 && (
                  <div style={{ fontSize: 13, color: 'var(--brand-text-muted)' }}>Keine Daten</div>
                )}
              </div>

              {/* Kosten nach User */}
              <div style={{ background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Nach Mitarbeiter</div>
                {(aiCosts.by_user || []).map((u, i) => (
                  <div key={u.user_id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--brand-warm-gray)' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--brand-navy)' }}>{u.name}</div>
                      <div style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>{u.calls} Aufrufe</div>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)' }}>${u.cost_usd?.toFixed(4)}</div>
                  </div>
                ))}
                {(aiCosts.by_user || []).length === 0 && (
                  <div style={{ fontSize: 13, color: 'var(--brand-text-muted)' }}>Keine Daten</div>
                )}
              </div>
            </div>

            {/* Tagesverlauf */}
            {(aiCosts.by_day || []).length > 0 && (
              <div style={{ background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem', marginBottom: 20 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Tagesverlauf</div>
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80 }}>
                  {aiCosts.by_day.map((d, i) => {
                    const maxCost = Math.max(...aiCosts.by_day.map(x => x.cost_usd), 0.001);
                    const height = Math.max(4, (d.cost_usd / maxCost) * 70);
                    return (
                      <div key={d.date} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <div style={{ fontSize: 9, color: 'var(--brand-text-muted)' }}>{d.calls}</div>
                        <div style={{ width: '100%', height, background: 'var(--brand-gold)', borderRadius: '3px 3px 0 0', minWidth: 8 }} title={`${d.date}: $${d.cost_usd} (${d.calls} Calls)`} />
                        <div style={{ fontSize: 8, color: 'var(--brand-text-muted)', whiteSpace: 'nowrap' }}>{d.date.slice(8)}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Briefing-Einstellungen */}
            {briefingSettings && (
              <div style={{ background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 8, padding: '1.25rem' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 }}>Briefing-Einstellungen</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                  <label style={{ fontSize: 13, color: 'var(--brand-navy)' }}>Refreshes pro Mitarbeiter/Tag:</label>
                  <input type="number" min="0" max="10" value={newDailyLimit}
                    onChange={e => setNewDailyLimit(e.target.value)}
                    style={{ width: 60, padding: '4px 8px', fontSize: 13, border: '1px solid var(--brand-warm-gray)', borderRadius: 4 }}
                  />
                  <button onClick={saveDailyLimit}
                    style={{ padding: '5px 14px', fontSize: 12, border: '1px solid var(--brand-gold)', borderRadius: 4, background: 'var(--brand-gold-10, #f5f0e0)', color: 'var(--brand-navy)', cursor: 'pointer', fontWeight: 500 }}
                  >Speichern</button>
                </div>
                <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>
                  Heute: {briefingSettings.usage_today?.users_with_briefing} Briefings generiert, {briefingSettings.usage_today?.total_api_calls} API-Aufrufe gesamt
                  {' '}(Admin/Manager sind unbegrenzt)
                </div>
              </div>
            )}
          </div>
        ) : (
          <div style={{ padding: '2rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>Keine Daten verfuegbar.</div>
        )}
      </>)}

      {/* ══ Tab: E-Mail-Sichtbarkeit ══ */}
      {activeTab === 'mail-sichtbarkeit' && (<MailVisibilityTab token={token} />)}

      {/* ══ Tab: SB-Vorlagen ══ */}
      {activeTab === 'vorlagen' && (<SBVorlagenTab token={token} />)}

      {activeTab === 'doc-ai-kappung' && (<DocAiTruncationTab token={token} />)}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// E-Mail-Sichtbarkeit Tab (separate Komponente für Übersichtlichkeit)
// ═══════════════════════════════════════════════════════════════

function MailVisibilityTab({ token }) {
  const [visData, setVisData] = useState(null);
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editUser, setEditUser] = useState(null); // { id, name, email, role, targets }
  const [saving, setSaving] = useState(false);
  const [searchFilter, setSearchFilter] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/admin/mail-visibility`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
      fetch(`${API_URL}/admin/mail-visibility/mailboxes`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
    ]).then(([vis, mb]) => {
      setVisData(vis);
      setMailboxes(mb.mailboxes || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [token]);

  const card = {
    background: '#fff', border: '1px solid var(--brand-warm-gray)',
    borderRadius: 8, padding: '1.25rem', marginBottom: 16,
  };

  const handleSave = (userId, targets) => {
    setSaving(true);
    fetch(`${API_URL}/admin/mail-visibility/${userId}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ targets }),
    })
      .then(r => r.json())
      .then(() => {
        setSaving(false);
        setEditUser(null);
        load();
      })
      .catch(() => setSaving(false));
  };

  const handleClear = (userId) => {
    fetch(`${API_URL}/admin/mail-visibility/${userId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(() => { load(); setEditUser(null); })
      .catch(() => {});
  };

  if (loading) return (
    <div style={{ padding: '2rem', color: 'var(--brand-text-muted)', fontSize: 13 }}>Sichtbarkeitsregeln werden geladen...</div>
  );

  const users = visData?.users || [];
  const roleLabels = { admin: 'Admin', manager: 'Teamleiter', entwickler: 'Entwickler', mitarbeiter: 'Mitarbeiter', readonly: 'Nur-Lesen' };
  const roleColors = { admin: '#c0392b', manager: '#2980b9', entwickler: '#27ae60', mitarbeiter: 'var(--brand-navy)', readonly: '#888' };

  return (
    <>
      {/* Info-Box */}
      <div style={{
        ...card, borderLeft: '4px solid var(--brand-gold)',
        padding: '14px 20px', fontSize: 13, color: 'var(--brand-navy)',
      }}>
        <strong>So funktioniert die E-Mail-Sichtbarkeit:</strong>
        <ul style={{ margin: '8px 0 0', paddingLeft: 18, fontSize: 12, color: 'var(--brand-text-muted)', lineHeight: 1.8 }}>
          <li><strong>Admins</strong> sehen immer alle Postfaecher — keine Einschraenkung moeglich.</li>
          <li><strong>Teamleiter ohne Regeln</strong> sehen ebenfalls alles (Standard).</li>
          <li><strong>Teamleiter mit Regeln</strong> sehen nur die zugewiesenen Postfaecher.</li>
          <li><strong>Mitarbeiter, Entwickler, Nur-Lesen</strong> haben keinen Zugriff auf die E-Mail-Statistiken.</li>
        </ul>
      </div>

      {/* Suchfilter */}
      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          value={searchFilter}
          onChange={e => setSearchFilter(e.target.value)}
          placeholder="Nutzer suchen..."
          style={{
            padding: '8px 12px', border: '1px solid var(--brand-warm-gray)',
            borderRadius: 6, fontSize: 13, width: 280, fontFamily: 'var(--font-body)',
            color: 'var(--brand-navy)',
          }}
        />
      </div>

      {/* User-Liste */}
      <div style={card}>
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}><table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 600 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--brand-warm-gray)' }}>
              <th style={{ textAlign: 'left', padding: '8px', fontSize: 11, color: 'var(--brand-navy)', fontWeight: 600 }}>Nutzer</th>
              <th style={{ textAlign: 'left', padding: '8px', fontSize: 11, color: 'var(--brand-navy)', fontWeight: 600 }}>Rolle</th>
              <th style={{ textAlign: 'left', padding: '8px', fontSize: 11, color: 'var(--brand-navy)', fontWeight: 600 }}>Sichtbare Postfaecher</th>
              <th style={{ textAlign: 'right', padding: '8px', fontSize: 11, color: 'var(--brand-navy)', fontWeight: 600 }}>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {users
              .filter(u => u.role === 'admin' || u.role === 'manager')
              .filter(u => !searchFilter || u.name.toLowerCase().includes(searchFilter.toLowerCase()) || u.email.toLowerCase().includes(searchFilter.toLowerCase()))
              .map((u, i) => (
              <tr key={u.id} style={{
                borderBottom: '1px solid var(--brand-warm-gray)',
                background: i % 2 === 0 ? '#fff' : 'var(--brand-cream, #fafaf6)',
              }}>
                <td style={{ padding: '8px' }}>
                  <div style={{ fontWeight: 500, color: 'var(--brand-navy)' }}>{u.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>{u.email}</div>
                </td>
                <td style={{ padding: '8px' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 4,
                    background: `${roleColors[u.role] || '#888'}12`,
                    color: roleColors[u.role] || '#888',
                    textTransform: 'uppercase', letterSpacing: '0.3px',
                  }}>
                    {roleLabels[u.role] || u.role}
                  </span>
                </td>
                <td style={{ padding: '8px' }}>
                  {u.role === 'admin' ? (
                    <span style={{ fontSize: 12, color: 'var(--brand-text-muted)', fontStyle: 'italic' }}>Alle (Admin)</span>
                  ) : !u.has_restrictions ? (
                    <span style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>
                      Alle <span style={{ fontSize: 10, color: '#27ae60' }}>(Standard)</span>
                    </span>
                  ) : (
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {u.targets.map(email => (
                        <span key={email} style={{
                          fontSize: 11, padding: '1px 6px', borderRadius: 3,
                          background: 'var(--brand-cream)', border: '1px solid var(--brand-warm-gray)',
                          color: 'var(--brand-navy)',
                        }}>
                          {email.split('@')[0].toUpperCase()}
                        </span>
                      ))}
                      <span style={{ fontSize: 10, color: 'var(--brand-text-muted)', alignSelf: 'center' }}>
                        ({u.targets.length} Postfach{u.targets.length !== 1 ? 'er' : ''})
                      </span>
                    </div>
                  )}
                </td>
                <td style={{ padding: '8px', textAlign: 'right' }}>
                  {u.role !== 'admin' && (
                    <button
                      onClick={() => setEditUser({ ...u, targets: [...u.targets] })}
                      style={{
                        padding: '4px 12px', fontSize: 11, fontWeight: 500,
                        border: '1px solid var(--brand-warm-gray)', borderRadius: 4,
                        background: '#fff', cursor: 'pointer', color: 'var(--brand-navy)',
                        fontFamily: 'var(--font-body)',
                      }}
                    >
                      Bearbeiten
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </div>

      {/* Edit-Modal */}
      {editUser && (
        <MailVisibilityEditModal
          user={editUser}
          mailboxes={mailboxes}
          saving={saving}
          onSave={(targets) => handleSave(editUser.id, targets)}
          onClear={() => handleClear(editUser.id)}
          onClose={() => setEditUser(null)}
        />
      )}
    </>
  );
}


function MailVisibilityEditModal({ user, mailboxes, saving, onSave, onClear, onClose }) {
  const [selected, setSelected] = useState(new Set(user.targets.map(e => e.toLowerCase())));
  const [search, setSearch] = useState('');

  const toggle = (email) => {
    const next = new Set(selected);
    const key = email.toLowerCase();
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const selectAll = () => setSelected(new Set(mailboxes.map(m => m.email.toLowerCase())));
  const selectNone = () => setSelected(new Set());

  const filtered = mailboxes.filter(m =>
    !search || m.name.toLowerCase().includes(search.toLowerCase()) || m.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(11,29,58,0.5)',
      zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
    }} onClick={onClose}>
      <div style={{
        background: '#fff', borderRadius: 12, width: '100%', maxWidth: 560,
        maxHeight: '80vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid var(--brand-warm-gray)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--brand-navy)' }}>
              Postfach-Sichtbarkeit — {user.name}
            </div>
            <div style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 2 }}>
              {user.email} · {user.role === 'manager' ? 'Teamleiter' : user.role}
            </div>
            <div style={{ fontSize: 11, color: 'var(--brand-text-muted)', marginTop: 4 }}>
              Waehlen Sie die Postfaecher, die dieser Nutzer in den E-Mail-Statistiken sehen darf.
            </div>
          </div>
          <button onClick={onClose} style={{
            width: 32, height: 32, border: 'none', background: 'var(--brand-cream)',
            borderRadius: 6, cursor: 'pointer', fontSize: 18, color: 'var(--brand-navy-70)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>

        {/* Toolbar */}
        <div style={{
          padding: '10px 20px', borderBottom: '1px solid var(--brand-warm-gray)',
          display: 'flex', gap: 8, alignItems: 'center', background: 'var(--brand-cream, #fafaf6)',
        }}>
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Postfach suchen..."
            style={{
              flex: 1, padding: '6px 10px', border: '1px solid var(--brand-warm-gray)',
              borderRadius: 4, fontSize: 12, fontFamily: 'var(--font-body)', color: 'var(--brand-navy)',
            }}
          />
          <button onClick={selectAll} style={{
            padding: '5px 10px', fontSize: 11, border: '1px solid var(--brand-warm-gray)',
            borderRadius: 4, background: '#fff', cursor: 'pointer', color: 'var(--brand-navy)',
            fontFamily: 'var(--font-body)',
          }}>Alle</button>
          <button onClick={selectNone} style={{
            padding: '5px 10px', fontSize: 11, border: '1px solid var(--brand-warm-gray)',
            borderRadius: 4, background: '#fff', cursor: 'pointer', color: 'var(--brand-navy)',
            fontFamily: 'var(--font-body)',
          }}>Keine</button>
          <span style={{ fontSize: 11, color: 'var(--brand-text-muted)', whiteSpace: 'nowrap' }}>
            {selected.size} / {mailboxes.length}
          </span>
        </div>

        {/* Mailbox-Liste */}
        <div style={{ flex: 1, overflow: 'auto', padding: '8px 20px' }}>
          {filtered.map(mb => {
            const isSelected = selected.has(mb.email.toLowerCase());
            return (
              <label key={mb.email} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px',
                borderBottom: '1px solid var(--brand-warm-gray)', cursor: 'pointer',
                background: isSelected ? 'rgba(193,167,111,0.06)' : 'transparent',
                borderRadius: 4,
              }}>
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={() => toggle(mb.email)}
                  style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--brand-navy)' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: isSelected ? 500 : 400, color: 'var(--brand-navy)' }}>{mb.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>{mb.email}</div>
                </div>
                {isSelected && (
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#27ae60', flexShrink: 0 }} />
                )}
              </label>
            );
          })}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 20px', borderTop: '1px solid var(--brand-warm-gray)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <button onClick={onClear} style={{
            padding: '7px 14px', fontSize: 12, border: '1px solid var(--brand-warm-gray)',
            borderRadius: 6, background: '#fff', cursor: 'pointer', color: 'var(--brand-text-muted)',
            fontFamily: 'var(--font-body)',
          }}>
            Einschraenkung aufheben (alle sichtbar)
          </button>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{
              padding: '7px 14px', fontSize: 12, border: '1px solid var(--brand-warm-gray)',
              borderRadius: 6, background: '#fff', cursor: 'pointer', color: 'var(--brand-navy)',
              fontFamily: 'var(--font-body)',
            }}>Abbrechen</button>
            <button onClick={() => onSave([...selected])} disabled={saving} style={{
              padding: '7px 18px', fontSize: 12, fontWeight: 600, border: 'none',
              borderRadius: 6, background: 'var(--brand-navy)', color: '#fff',
              cursor: saving ? 'wait' : 'pointer', fontFamily: 'var(--font-body)',
            }}>
              {saving ? 'Speichern...' : `Speichern (${selected.size} Postfaecher)`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// SB-Vorlagen Tab (Schwarzes Brett Vorlagen-Editor + WhatsApp-Status)
// ═══════════════════════════════════════════════════════════════

function SBVorlagenTab({ token }) {
  const API_URL = process.env.REACT_APP_API_URL || '/api';
  const [vorlagen, setVorlagen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editKategorie, setEditKategorie] = useState(null);
  const [editHtml, setEditHtml] = useState('');
  const [editBetreffs, setEditBetreffs] = useState([]);
  const [editDefaults, setEditDefaults] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [newBetreff, setNewBetreff] = useState('');
  const [newDefaultKey, setNewDefaultKey] = useState('');
  const [newDefaultVal, setNewDefaultVal] = useState('');
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API_URL}/skills/schwarzes-brett/vorlagen`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(vData => { setVorlagen(vData); setLoading(false); })
      .catch(() => { setError('Vorlagen konnten nicht geladen werden.'); setLoading(false); });
  }, [token]);

  const openEditor = (v) => {
    setEditKategorie(v.kategorie);
    setEditHtml(v.template_html);
    setEditBetreffs([...(v.beispiel_betreffs || [])]);
    setEditDefaults({ ...(v.defaults || {}) });
    setSaveMsg(null);
    setShowPreview(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch(`${API_URL}/skills/schwarzes-brett/vorlagen/${editKategorie}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          template_html: editHtml,
          beispiel_betreffs: editBetreffs,
          defaults: editDefaults,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler beim Speichern');
      // wa_status aus Response uebernehmen
      const newWaStatus = data.wa_status || 'none';
      const waSync = data.wa_sync;
      let msg = 'Vorlage gespeichert!';
      if (waSync && waSync.success) {
        msg = 'Vorlage gespeichert! WhatsApp-Template wird bei Meta eingereicht. Status: pending.';
      } else if (waSync && !waSync.success) {
        msg = `Vorlage gespeichert! WhatsApp-Sync Fehler: ${waSync.error || 'Unbekannt'}`;
      }
      setSaveMsg(msg);
      setVorlagen(prev => prev.map(v => v.kategorie === editKategorie
        ? { ...v, template_html: editHtml, beispiel_betreffs: editBetreffs, defaults: editDefaults, updated_at: data.updated_at, wa_status: newWaStatus }
        : v
      ));
    } catch (e) {
      setSaveMsg(`Fehler: ${e.message}`);
    }
    setSaving(false);
  };

  const removeBetreff = (idx) => setEditBetreffs(prev => prev.filter((_, i) => i !== idx));
  const addBetreff = () => {
    if (newBetreff.trim()) {
      setEditBetreffs(prev => [...prev, newBetreff.trim()]);
      setNewBetreff('');
    }
  };
  const removeDefault = (key) => setEditDefaults(prev => { const n = { ...prev }; delete n[key]; return n; });
  const addDefault = () => {
    if (newDefaultKey.trim()) {
      setEditDefaults(prev => ({ ...prev, [newDefaultKey.trim()]: newDefaultVal }));
      setNewDefaultKey('');
      setNewDefaultVal('');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/skills/schwarzes-brett/vorlagen/sync`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Sync fehlgeschlagen');
      // Update wa_status in vorlagen
      const statusMap = {};
      (data.templates || []).forEach(t => { statusMap[t.kategorie] = t.wa_status; });
      setVorlagen(prev => prev.map(v => ({
        ...v,
        wa_status: statusMap[v.kategorie] || v.wa_status,
        wa_last_synced: new Date().toISOString(),
      })));
      setSaveMsg('WhatsApp-Status synchronisiert!');
    } catch (e) {
      setSaveMsg(`Sync-Fehler: ${e.message}`);
    }
    setSyncing(false);
  };

  const waStatusBadge = (status) => {
    const colors = { approved: { bg: '#e8f5e9', text: '#2e7d32' }, pending: { bg: '#fff8e1', text: '#f57f17' }, rejected: { bg: '#ffebee', text: '#c62828' }, none: { bg: '#f5f5f5', text: '#9e9e9e' }, not_found: { bg: '#f5f5f5', text: '#9e9e9e' } };
    const c = colors[status] || colors.none;
    return { padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: c.bg, color: c.text, display: 'inline-block' };
  };

  const waCardBorder = (status) => {
    if (status === 'pending') return '2px solid #f57f17';
    if (status === 'rejected') return '2px solid #c62828';
    return '1px solid var(--brand-warm-gray)';
  };

  const prioColors = { hoch: '#f44336', mittel: '#ff9800', normal: '#4CAF50', niedrig: '#9e9e9e' };
  const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--brand-navy)', marginBottom: 4, display: 'block', textTransform: 'uppercase', letterSpacing: '0.3px' };
  const inputStyle = { width: '100%', padding: '8px 12px', fontSize: 13, border: '1px solid var(--brand-warm-gray)', borderRadius: 4, fontFamily: 'var(--font-body)', boxSizing: 'border-box' };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--brand-text-muted)' }}>Vorlagen werden geladen...</div>;
  if (error) return <div style={{ padding: 20, color: 'var(--brand-danger)' }}>{error}</div>;

  // Editor-Ansicht
  if (editKategorie) {
    const vorlage = vorlagen.find(v => v.kategorie === editKategorie);
    const waStatus_ = vorlage?.wa_status || 'none';
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* Back + Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setEditKategorie(null)} style={{
            padding: '6px 14px', fontSize: 12, border: '1px solid var(--brand-warm-gray)', borderRadius: 4,
            background: '#fff', color: 'var(--brand-text)', cursor: 'pointer', fontFamily: 'var(--font-body)',
          }}>← Zurueck</button>
          <div style={{ fontSize: 18, fontFamily: 'var(--font-headline)', color: 'var(--brand-navy)' }}>
            {vorlage?.icon} {vorlage?.display_name}
          </div>
          <span style={{
            padding: '3px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600,
            background: prioColors[vorlage?.prioritaet] || '#9e9e9e', color: '#fff',
          }}>{vorlage?.prioritaet}</span>
          <span style={waStatusBadge(waStatus_)}>WhatsApp: {waStatus_}</span>
        </div>

        {/* WhatsApp-Info */}
        {vorlage?.wa_template_name && (
          <div style={{
            padding: '10px 14px', borderRadius: 6, fontSize: 12,
            background: waStatus_ === 'pending' ? '#fff8e1' : waStatus_ === 'rejected' ? '#ffebee' : '#e3f2fd',
            border: `1px solid ${waStatus_ === 'pending' ? '#ffe082' : waStatus_ === 'rejected' ? '#ef9a9a' : '#bbdefb'}`,
          }}>
            <strong>WhatsApp-Template:</strong> {vorlage.wa_template_name} | <strong>Variablen:</strong> {(vorlage.pflicht_vars || []).length + (vorlage.optional_vars || []).length} | <strong>Status:</strong> {waStatus_}
            {waStatus_ === 'pending' && <span style={{ marginLeft: 12, color: '#f57f17', fontWeight: 600 }}>WhatsApp pending — nicht aktiv fuer Versand</span>}
            {waStatus_ === 'rejected' && <span style={{ marginLeft: 12, color: '#c62828', fontWeight: 600 }}>WhatsApp rejected — Template wurde von Meta abgelehnt</span>}
            {vorlage.wa_last_synced && <span style={{ marginLeft: 12, color: '#666', fontSize: 10 }}>Zuletzt synchronisiert: {new Date(vorlage.wa_last_synced).toLocaleString('de-DE')}</span>}
          </div>
        )}

        {/* Pflicht-Variablen (readonly) */}
        <div style={{ padding: '10px 14px', background: 'var(--brand-cream)', borderRadius: 6, border: '1px solid var(--brand-warm-gray)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-navy)', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 6 }}>Pflicht-Variablen (nicht editierbar)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(vorlage?.pflicht_vars || []).map(v => (
              <span key={v} style={{ padding: '3px 8px', fontSize: 11, background: '#fff', border: '1px solid var(--brand-warm-gray)', borderRadius: 3, fontFamily: 'monospace' }}>{'{{' + v + '}}'}</span>
            ))}
          </div>
        </div>

        {/* Optionale Variablen (readonly) */}
        <div style={{ padding: '10px 14px', background: '#f8f9fa', borderRadius: 6, border: '1px solid var(--brand-warm-gray)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--brand-text-muted)', textTransform: 'uppercase', letterSpacing: '0.3px', marginBottom: 6 }}>Optionale Variablen</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {(vorlage?.optional_vars || []).map(v => (
              <span key={v} style={{ padding: '3px 8px', fontSize: 11, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 3, fontFamily: 'monospace', color: 'var(--brand-text-muted)' }}>{'{{' + v + '}}'}</span>
            ))}
          </div>
        </div>

        {/* Beispiel-Betreffs */}
        <div>
          <label style={labelStyle}>Beispiel-Betreffs</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {editBetreffs.map((b, i) => (
              <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input value={b} onChange={e => setEditBetreffs(prev => prev.map((x, j) => j === i ? e.target.value : x))}
                  style={{ ...inputStyle, flex: 1 }} />
                <button onClick={() => removeBetreff(i)} style={{ padding: '6px 10px', fontSize: 14, border: 'none', background: 'var(--brand-danger-bg)', color: 'var(--brand-danger)', borderRadius: 4, cursor: 'pointer', lineHeight: 1 }} title="Entfernen">x</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6 }}>
              <input value={newBetreff} onChange={e => setNewBetreff(e.target.value)} onKeyDown={e => e.key === 'Enter' && addBetreff()}
                placeholder="Neuen Beispiel-Betreff hinzufuegen..." style={{ ...inputStyle, flex: 1 }} />
              <button onClick={addBetreff} style={{ padding: '6px 14px', fontSize: 12, border: '1px solid var(--brand-gold)', borderRadius: 4, background: 'var(--brand-cream)', color: 'var(--brand-navy)', cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' }}>+ Hinzufuegen</button>
            </div>
          </div>
        </div>

        {/* HTML-Template */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <label style={{ ...labelStyle, marginBottom: 0 }}>HTML-Template</label>
            <button onClick={() => setShowPreview(!showPreview)} style={{ fontSize: 11, padding: '3px 10px', border: '1px solid var(--brand-warm-gray)', borderRadius: 3, background: showPreview ? 'var(--brand-navy)' : '#fff', color: showPreview ? '#fff' : 'var(--brand-text)', cursor: 'pointer' }}>
              {showPreview ? 'Bearbeiten' : 'Vorschau'}
            </button>
          </div>
          {showPreview ? (
            <div style={{ ...inputStyle, minHeight: 300, padding: 16, fontSize: 13, lineHeight: 1.6, overflow: 'auto', background: '#fafafa' }} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(editHtml, { ALLOWED_TAGS: ['h1','h2','h3','h4','p','br','strong','em','b','i','u','ul','ol','li','table','thead','tbody','tr','th','td','div','span','hr','a','img'], ALLOWED_ATTR: ['style','class','href','src','alt'] }) }} />
          ) : (
            <textarea value={editHtml} onChange={e => setEditHtml(e.target.value)} style={{ ...inputStyle, minHeight: 300, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.5 }} />
          )}
        </div>

        {/* Defaults (Key-Value Editor) */}
        <div>
          <label style={labelStyle}>Standard-Werte (Defaults)</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {Object.entries(editDefaults).map(([k, v]) => (
              <div key={k} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <input value={k} disabled style={{ ...inputStyle, flex: '0 0 180px', background: '#f5f5f5', fontFamily: 'monospace', fontSize: 12 }} />
                <input value={v} onChange={e => setEditDefaults(prev => ({ ...prev, [k]: e.target.value }))} style={{ ...inputStyle, flex: 1 }} />
                <button onClick={() => removeDefault(k)} style={{ padding: '6px 10px', fontSize: 14, border: 'none', background: 'var(--brand-danger-bg)', color: 'var(--brand-danger)', borderRadius: 4, cursor: 'pointer', lineHeight: 1 }} title="Entfernen">x</button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input value={newDefaultKey} onChange={e => setNewDefaultKey(e.target.value)} placeholder="Schluessel" style={{ ...inputStyle, flex: '0 0 180px', fontFamily: 'monospace', fontSize: 12 }} />
              <input value={newDefaultVal} onChange={e => setNewDefaultVal(e.target.value)} placeholder="Wert" onKeyDown={e => e.key === 'Enter' && addDefault()} style={{ ...inputStyle, flex: 1 }} />
              <button onClick={addDefault} style={{ padding: '6px 14px', fontSize: 12, border: '1px solid var(--brand-gold)', borderRadius: 4, background: 'var(--brand-cream)', color: 'var(--brand-navy)', cursor: 'pointer', fontFamily: 'var(--font-body)', whiteSpace: 'nowrap' }}>+</button>
            </div>
          </div>
        </div>

        {/* Speichern */}
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <button onClick={handleSave} disabled={saving} style={{
            padding: '10px 24px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 4,
            background: saving ? 'var(--brand-warm-gray)' : 'var(--brand-navy)', color: '#fff',
            cursor: saving ? 'default' : 'pointer', fontFamily: 'var(--font-body)',
          }}>{saving ? 'Wird gespeichert...' : 'Vorlage speichern'}</button>
          {saveMsg && <span style={{ fontSize: 12, color: saveMsg.startsWith('Fehler') ? 'var(--brand-danger)' : 'var(--brand-success)' }}>{saveMsg}</span>}
        </div>
      </div>
    );
  }

  // Karten-Uebersicht
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--brand-text-muted)' }}>
          Hier koennen Sie die Schwarzes-Brett-Vorlagen bearbeiten. Aenderungen werden sofort fuer neue Postings wirksam.
        </div>
        <button onClick={handleSync} disabled={syncing} style={{
          padding: '8px 16px', fontSize: 12, fontWeight: 600, border: '1px solid var(--brand-navy)',
          borderRadius: 4, background: syncing ? 'var(--brand-warm-gray)' : '#fff',
          color: 'var(--brand-navy)', cursor: syncing ? 'default' : 'pointer',
          fontFamily: 'var(--font-body)', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {syncing ? 'Synchronisiere...' : 'WhatsApp synchronisieren'}
        </button>
      </div>
      {saveMsg && (
        <div style={{ padding: '8px 14px', marginBottom: 12, borderRadius: 4, fontSize: 12,
          background: saveMsg.includes('Fehler') || saveMsg.includes('Error') ? '#ffebee' : '#e8f5e9',
          color: saveMsg.includes('Fehler') || saveMsg.includes('Error') ? '#c62828' : '#2e7d32',
        }}>{saveMsg}</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
        {vorlagen.map(v => {
          const ws = v.wa_status || 'none';
          return (
            <div key={v.kategorie} onClick={() => openEditor(v)} style={{
              background: '#fff', borderRadius: 8, padding: '16px 20px', cursor: 'pointer',
              border: waCardBorder(ws), transition: 'all 0.15s',
            }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--brand-gold)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => {
                e.currentTarget.style.border = waCardBorder(ws);
                e.currentTarget.style.transform = 'none';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 24 }}>{v.icon}</span>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={waStatusBadge(ws)}>WA: {ws}</span>
                  <span style={{ padding: '2px 8px', borderRadius: 3, fontSize: 10, fontWeight: 600, background: prioColors[v.prioritaet] || '#9e9e9e', color: '#fff' }}>{v.prioritaet}</span>
                </div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--brand-navy)', marginBottom: 4 }}>{v.display_name}</div>
              <div style={{ fontSize: 11, color: 'var(--brand-text-muted)' }}>
                {(v.pflicht_vars || []).length} Pflicht-Vars | {(v.beispiel_betreffs || []).length} Beispiel-Betreffs
              </div>
              {/* WhatsApp pending/rejected Hinweis */}
              {ws === 'pending' && (
                <div style={{ marginTop: 8, padding: '4px 8px', borderRadius: 4, background: '#fff8e1', border: '1px solid #ffe082', fontSize: 10, color: '#f57f17', fontWeight: 600 }}>
                  WhatsApp pending — nicht aktiv fuer Versand
                </div>
              )}
              {ws === 'rejected' && (
                <div style={{ marginTop: 8, padding: '4px 8px', borderRadius: 4, background: '#ffebee', border: '1px solid #ef9a9a', fontSize: 10, color: '#c62828', fontWeight: 600 }}>
                  WhatsApp rejected — Template abgelehnt
                </div>
              )}
              {v.wa_template_name && ws !== 'pending' && ws !== 'rejected' && (
                <div style={{ marginTop: 8, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <span style={{ fontSize: 10, color: 'var(--brand-text-muted)' }}>{v.wa_template_name}</span>
                </div>
              )}
              {v.updated_at && (
                <div style={{ fontSize: 10, color: 'var(--brand-text-muted)', marginTop: 6 }}>
                  Zuletzt geaendert: {new Date(v.updated_at).toLocaleDateString('de-DE')}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Doc-AI Kappungs-Rate (#203 Teil C): wie oft wurde eine Modellantwort am Output-
// Deckel gekappt (Teilergebnis). Quelle sind Redis-Zähler, die der attachment-parser-
// Adapter auf dem Live-Pfad best-effort mitschreibt (Cache-Hits zählen nicht mit → die
// Rate unterzählt um den Cache-Hit-Anteil). Selbstständige Tab-Komponente wie
// MailVisibilityTab / SBVorlagenTab.
function DocAiTruncationTab({ token }) {
  const API_URL = process.env.REACT_APP_API_URL || '/api';
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_URL}/admin/attachment-parser/truncation?days=${days}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => (r.ok ? r.json() : Promise.reject(r)))
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('Kappungs-Statistik konnte nicht geladen werden.'); setLoading(false); });
  }, [token, days]);

  const pct = (r) => `${(r * 100).toFixed(1)} %`;
  const rateColor = (r) => (r >= 0.10 ? '#c0392b' : r >= 0.02 ? '#e67e22' : '#27ae60');
  const displayDay = (d) =>
    d && d.length === 8 ? `${d.slice(6, 8)}.${d.slice(4, 6)}.${d.slice(0, 4)}` : d;

  return (
    <div style={{ maxWidth: 900 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>✂️ Doc-AI Kappungs-Rate</h3>
        <label style={{ fontSize: 13, color: 'var(--brand-text-muted)' }}>
          Zeitraum:{' '}
          <select value={days} onChange={e => setDays(Number(e.target.value))} style={{ padding: '4px 8px' }}>
            <option value={7}>7 Tage</option>
            <option value={14}>14 Tage</option>
            <option value={30}>30 Tage</option>
          </select>
        </label>
      </div>
      <p style={{ fontSize: 12, color: 'var(--brand-text-muted)', marginTop: 0 }}>
        Anteil der Doc-AI-Extraktionen, deren Modellantwort am Output-Deckel gekappt wurde
        (Teilergebnis, <code>truncated_output</code>). Nur Live-Calls (Dedup-Cache-Hits laufen nicht
        durchs Modell) — die Rate unterzählt daher um den Cache-Hit-Anteil. Autoritatives, cache-festes
        Signal je Dokument ist das Feld <code>unvollstaendig</code>.
      </p>

      {loading && <p>Lädt…</p>}
      {error && <p style={{ color: '#c0392b' }}>{error}</p>}

      {data && !loading && !error && (() => {
        const totals = data.totals || { calls: 0, truncated: 0, rate: 0 };
        const rows = data.rows || [];
        return (
        <>
          <div style={{ display: 'flex', gap: 24, margin: '16px 0', flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700, color: rateColor(totals.rate) }}>
                {pct(totals.rate)}
              </div>
              <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>Kappungs-Rate gesamt</div>
            </div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{totals.truncated}</div>
              <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>gekappt</div>
            </div>
            <div>
              <div style={{ fontSize: 28, fontWeight: 700 }}>{totals.calls}</div>
              <div style={{ fontSize: 12, color: 'var(--brand-text-muted)' }}>Live-Calls</div>
            </div>
          </div>

          {rows.length === 0 ? (
            <p style={{ color: 'var(--brand-text-muted)' }}>Keine Doc-AI-Calls im Zeitraum.</p>
          ) : (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
            <table style={{ width: '100%', minWidth: 600, borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--brand-border, #ddd)' }}>
                  <th style={{ padding: '6px 8px' }}>Tenant</th>
                  <th style={{ padding: '6px 8px' }}>Tag</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Calls</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Gekappt</th>
                  <th style={{ padding: '6px 8px', textAlign: 'right' }}>Rate</th>
                </tr>
              </thead>
              <tbody>
                {[...rows]
                  .sort((a, b) => (b.day.localeCompare(a.day) || a.tenant.localeCompare(b.tenant)))
                  .map((r) => (
                    <tr key={`${r.tenant}:${r.day}`} style={{ borderBottom: '1px solid var(--brand-border, #eee)' }}>
                      <td style={{ padding: '6px 8px' }}>{r.tenant}</td>
                      <td style={{ padding: '6px 8px' }}>{displayDay(r.day)}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.calls}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right' }}>{r.truncated}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: rateColor(r.rate) }}>
                        {pct(r.rate)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            </div>
          )}
        </>
        );
      })()}
    </div>
  );
}
